import * as i0 from '@angular/core';
import { Injectable, InjectionToken, inject, PLATFORM_ID, Inject, LOCALE_ID, Optional, DestroyRef, TransferState, makeStateKey, Directive, Input, Host, HostListener, Pipe, makeEnvironmentProviders } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, ReplaySubject, of, BehaviorSubject, tap as tap$1, EMPTY, merge, filter as filter$1, map as map$1, catchError as catchError$1, firstValueFrom, take as take$1, switchMap as switchMap$1, debounceTime, from } from 'rxjs';
import { isPlatformBrowser, isPlatformServer, Location, DOCUMENT } from '@angular/common';
import { GetCartUseCase, AddToCartUseCase, UpdateCartUseCase, RemoveFromCartUseCase, DeleteCartUseCase, GetProductsByFiltersUseCase, GetProductWithMetaproductsUseCase, GetProductBySkuUseCase, GetRelatedProductsUseCase, GetProductsByCriteriaUseCase, GetProductsByCategoryUseCase, GetProductsByFeedUseCase, oAuthUseCase, M2MTokenUseCase, GetZendeskContactFormUseCase, SendContactFormUseCase, registerUseCase, getCustomerUseCase, tryGetCustomerUseCase, subscribeToNewsletterUseCase, loginM2MUseCase, updateCustomerInfoUseCase, forgotPasswordUseCase, resetPasswordUseCase, removeCredentialsM2MUseCase, isLoggedUseCase, loginUseCase, logOutUseCase, GetWishlistUseCase, UpdateWishlistUseCase } from '@scalefast/sam';
import { tap, catchError, map, shareReplay, filter, take, share, switchMap, first, finalize } from 'rxjs/operators';
import * as i1 from '@scalefast/config-angular';
import { BuilderConfigService } from '@scalefast/config-angular';
import * as i2 from '@angular/common/http';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import * as i3 from 'ngx-cookie-service';
import * as i6 from '@scalefast/core';
import { CATEGORY_FILTERS_OPTIONS, isValidLogLevel, logLevelValueMapping, getProductCanonicalSlug, PageLayout, MetaproductSelectorBase, BuilderContentError, PageComponents } from '@scalefast/core';
import * as i1$1 from '@angular/router';
import { NavigationEnd, ActivationEnd, Router } from '@angular/router';
import * as i1$2 from '@builder.io/angular';
class Fixtures {
  static {
    this.Cart = class {
      static {
        this.success = 'Product successfully added to you cart!';
      }
      static getAddedProductCorrectly() {
        return Fixtures.Cart.success;
      }
    };
  }
  static {
    this.generic = class {
      static responseSuccess() {
        const response = {
          result: {
            status: '0',
            errcode: '0',
            msg: 'This is a mock'
          },
          response_data: true
        };
        return response;
      }
      static responseError() {
        return false;
      }
    };
  }
}
let AlertMessageService = /*#__PURE__*/(() => {
  class AlertMessageService {
    constructor() {
      this.subject = new Subject();
    }
    sendMessage(message, type) {
      this.subject.next({
        message: message,
        type: type
      });
    }
    clearMessage() {
      const emptyAlertMessage = {
        message: '',
        type: ''
      };
      this.subject.next(emptyAlertMessage);
    }
    getMessage() {
      return this.subject.asObservable();
    }
    static {
      this.ɵfac = function AlertMessageService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AlertMessageService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AlertMessageService,
        factory: AlertMessageService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AlertMessageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/** It contains the information needed to indicate what entries need to be changed when an item is updated */
class StoreDependency {
  constructor(type, externalResourceId, resource, revalidationObject, lastUpdatedTimestamp, timeToLive) {
    this.type = type;
    this.externalResourceId = externalResourceId;
    this.resource = resource;
    this.revalidationObject = revalidationObject;
    this.lastUpdatedTimestamp = lastUpdatedTimestamp;
    this.timeToLive = timeToLive;
    this.cachedTimestamp = Date.now();
  }
}

/*
  The purpose of this class is to bring you the correct structure of Dependency depending on the type of the Dependency you want
*/
class DependencyBuilder {
  static product(productId) {
    return new StoreDependency('product', productId);
  }
  static productCollection(productId) {
    return new StoreDependency('productCollection', productId);
  }
  static manual() {
    return new StoreDependency('manual', 'manualResourceId');
  }
  static maintenance() {
    return new StoreDependency('maintenance', 'maintenanceResourceId');
  }
  static disableSSR() {
    return new StoreDependency('disableSSR', 'disableSSRResourceId');
  }
  // FIXME: We should add a debug property in Dependency instead of abusing the ExternalResourceId for the message
  static error(message) {
    return new StoreDependency('error', message);
  }
  static builder(builderId) {
    return new StoreDependency('builder', builderId);
  }
  static revalidableBuilder(builderId, revalidationObject) {
    return new StoreDependency('revalidableBuilder', builderId, '', revalidationObject);
  }
  static contentful() {
    return new StoreDependency('contentful', 'contentfulId');
  }
  static revalidableContentful(contentfulId, revalidationObject) {
    return new StoreDependency('revalidableContentful', contentfulId, '', revalidationObject);
  }
  static notFound(message) {
    return new StoreDependency('notFound', message);
  }
  static expirable(timeToLive) {
    return new StoreDependency('expirable', 'expirableId', undefined, undefined, undefined, timeToLive);
  }
}

/** Mecachis/SSR server shared injection tokens */
// Request response tokens for server side rendering some of this will be solved by
// https://github.com/angular/angular/discussions/56785
const EXPRESS_REQUEST = new InjectionToken('REQUEST');
const EXPRESS_RESPONSE = new InjectionToken('RESPONSE');
const SERVER_LOGGER = new InjectionToken('SERVER_LOGGER');
// Token for ensuring server providers are only optional with ng serve
const SERVER_PROVIDERS_REQUIRED = new InjectionToken('SERVER_PROVIDERS_REQUIRED');
const MECACHIS_DEPENDENCY_COLLECTION = new InjectionToken('dependencyCollection');
const MECACHIS_DEPENDENCY_SERVICE = new InjectionToken('MeCachisDependencyService');
let MeCachisDependencyServerSideService = /*#__PURE__*/(() => {
  class MeCachisDependencyServerSideService {
    constructor() {
      const injectedCollection = inject(MECACHIS_DEPENDENCY_COLLECTION, {
        optional: true
      });
      const serverProvidersRequired = inject(SERVER_PROVIDERS_REQUIRED, {
        optional: true
      });
      if (!injectedCollection && serverProvidersRequired) {
        throw new Error('MECACHIS_DEPENDENCY_COLLECTION inject token not available and required');
      }
      this.#dependencyCollection = injectedCollection ?? [];
    }
    #dependencyCollection;
    addProducts(products) {
      products.forEach(product => {
        this.addProduct(product.id);
      });
    }
    addProduct(productId) {
      const dependency = DependencyBuilder.product(productId.toString());
      this.#dependencyCollection.push(dependency);
    }
    addMaintenanceDependency() {
      const dependency = DependencyBuilder.maintenance();
      this.#dependencyCollection.push(dependency);
    }
    addDisableSSRDependency() {
      const disableSSRDependency = DependencyBuilder.disableSSR();
      this.#dependencyCollection.push(disableSSRDependency);
    }
    addBuilderDependency(builderId) {
      const builderIODependency = DependencyBuilder.builder(builderId);
      this.#dependencyCollection.push(builderIODependency);
    }
    addRevalidableBuilderDependency(builderId, revalidationObject) {
      const revalidableBuilderIODependency = DependencyBuilder.revalidableBuilder(builderId, revalidationObject);
      this.#dependencyCollection.push(revalidableBuilderIODependency);
    }
    addContentfulDependency() {
      const contentfulDependency = DependencyBuilder.contentful();
      this.#dependencyCollection.push(contentfulDependency);
    }
    addRevalidableContentfulDependency(contentfulId, revalidationObject) {
      const smartBuilderIODependency = DependencyBuilder.revalidableContentful(contentfulId, revalidationObject);
      this.#dependencyCollection.push(smartBuilderIODependency);
    }
    addNotFoundDependency(message) {
      const contentfulDependency = DependencyBuilder.notFound(message);
      this.#dependencyCollection.push(contentfulDependency);
    }
    addErrorDependency(message) {
      const errorDependency = DependencyBuilder.error(message);
      this.#dependencyCollection.push(errorDependency);
    }
    addExpirableDependency(timeToLive) {
      const expirableDependency = DependencyBuilder.expirable(timeToLive);
      this.#dependencyCollection.push(expirableDependency);
    }
    static {
      this.ɵfac = function MeCachisDependencyServerSideService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MeCachisDependencyServerSideService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MeCachisDependencyServerSideService,
        factory: MeCachisDependencyServerSideService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MeCachisDependencyServerSideService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class MeCachisDependencyServiceNoOp {
  addProducts(products) {
    //
  }
  addContentfulDependency() {
    //
  }
  addProduct(productId) {
    //
  }
  addMaintenanceDependency() {
    //
  }
  addDisableSSRDependency() {
    //
  }
  addBuilderDependency(builderId) {
    //
  }
  addRevalidableBuilderDependency(builderId, revalidableObject) {
    //
  }
  addRevalidableContentfulDependency(contentfulId, revalidableContent) {
    //
  }
  addNotFoundDependency(message) {
    //
  }
  addErrorDependency(message) {
    //
  }
  addExpirableDependency(timeToLive) {
    //
  }
}
let SamAngularService = /*#__PURE__*/(() => {
  class SamAngularService {
    constructor(samConfigService, httpClient, cookieService, meCachisDependencyService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    platformId) {
      this.samConfigService = samConfigService;
      this.samContext = JSON.parse(JSON.stringify(this.samConfigService.get()));
      this.samAngularContext = this.samContext;
      this.samAngularContext.drivers = {
        httpClient,
        cookieService,
        meCachisDependencyService,
        platformService: {
          isBrowser: () => isPlatformBrowser(platformId),
          isServer: () => false,
          isServerSideRendering: () => isPlatformServer(platformId)
        }
      };
    }
    getContext() {
      return this.samAngularContext;
    }
    static {
      this.ɵfac = function SamAngularService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SamAngularService)(i0.ɵɵinject(i1.SamConfigService), i0.ɵɵinject(i2.HttpClient), i0.ɵɵinject(i3.CookieService), i0.ɵɵinject(MECACHIS_DEPENDENCY_SERVICE), i0.ɵɵinject(PLATFORM_ID));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SamAngularService,
        factory: SamAngularService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SamAngularService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LocalStorageService = /*#__PURE__*/(() => {
  class LocalStorageService {
    constructor(checkoutConfigService) {
      this.checkoutConfigService = checkoutConfigService;
      this.CART_TOKEN_KEY = '';
      this.CART_PROVIDER_KEY = '';
      this.checkoutConfig = this.checkoutConfigService.get();
      this.CART_TOKEN_KEY = this.checkoutConfig.xApi.cartTokenKey;
      this.CART_PROVIDER_KEY = this.checkoutConfig.xApi.cartProviderKey;
    }
    createToken() {
      return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }
    getToken() {
      let token = this.getItem(this.CART_TOKEN_KEY) || '';
      if (!token) {
        token = this.createToken();
        this.setItem(this.CART_TOKEN_KEY, token);
      }
      return token;
    }
    getProviderId() {
      return this.getItem(this.CART_PROVIDER_KEY) || '';
    }
    setProviderId() {
      this.setItem(this.CART_PROVIDER_KEY, this.checkoutConfig.xApi.clientId);
    }
    getItem(key) {
      const item = localStorage.getItem(key);
      if (!item) {
        return null;
      }
      return JSON.parse(item);
    }
    setItem(key, item) {
      if (!item) {
        return;
      }
      localStorage.setItem(key, JSON.stringify(item));
    }
    removeItem(key) {
      localStorage.removeItem(key);
    }
    static {
      this.ɵfac = function LocalStorageService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LocalStorageService)(i0.ɵɵinject(i1.CheckoutConfigService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LocalStorageService,
        factory: LocalStorageService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LocalStorageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CartService = /*#__PURE__*/(() => {
  class CartService {
    constructor(samAngularService, checkoutConfigService, storeConfigService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    platformId,
    // PlatformId needs to be an Object
    localStorage) {
      this.samAngularService = samAngularService;
      this.checkoutConfigService = checkoutConfigService;
      this.storeConfigService = storeConfigService;
      this.platformId = platformId;
      this.localStorage = localStorage;
      this.cartData = {
        cartToken: '',
        cartProvider: ''
      };
      this.cartChanges$$ = new ReplaySubject();
      this.samContext = this.samAngularService.getContext();
      this.checkoutConfig = this.checkoutConfigService.get();
      this.storeConfig = this.storeConfigService.get();
      this.MAX_ITEM_QUANTITY = this.storeConfig.maxItemQuantity;
      this.maxOrderQuantity = this.storeConfig.maxOrderQuantity;
      this.initializeCart();
      if (isPlatformBrowser(this.platformId)) {
        this.localStorage.setProviderId();
        this.cartData = {
          cartToken: this.localStorage.getToken(),
          cartProvider: this.localStorage.getProviderId()
        };
        this.getCart$().pipe(takeUntilDestroyed()).subscribe(data => data);
      }
    }
    initializeCart() {
      this.emptyCart = {
        nbProducts: 0,
        products: [],
        /**
         * not used currently. We may need to add this when we have project territory support or when we connect more API's
         */
        // store: {
        //   projectId: this.samContext.v4.projectId,
        //   territoryId: this.samContext.v4.territoryId,
        //   entity: this.samContext.v4.entity,
        // },
        price: 0
      };
      this.emptyCart$ = of(this.emptyCart);
      this.cart$ = new BehaviorSubject(this.emptyCart);
    }
    getCart$() {
      return GetCartUseCase(this.samContext, this.checkoutConfig).execute(this.cartData)?.pipe(tap(response => {
        this.cart$.next(response);
      }), catchError(() => this.emptyCart$));
    }
    getCount$() {
      return this.cart$.pipe(map(shoppingCart => shoppingCart.products.reduce((total, item) => total + item.quantity, 0)));
    }
    getCartProductQuantity$(id) {
      return this.cart$.pipe(map(shoppingCart => {
        const filteredCartProduct = shoppingCart.products.filter(cardProduct => cardProduct.productId === id)[0];
        if (!filteredCartProduct) {
          return 0;
        }
        return filteredCartProduct.quantity;
      }));
    }
    getTotal$() {
      return this.cart$.pipe(map(shoppingCart => shoppingCart.products.reduce((total, item) => total + item.price * item.quantity, 0)));
    }
    getProducts() {
      const shoppingCart = {
        ...this.cart$.value
      };
      return shoppingCart.products || this.emptyCart;
    }
    addToCart$(product) {
      const products = this.getProducts();
      if (!product || !products) return this.emptyCart$;
      const productQuantityAdded = product.quantity;
      const maxItemQuantity = this.MAX_ITEM_QUANTITY;
      const params = {
        products,
        product,
        ...this.cartData,
        maxItemQuantity
      };
      return AddToCartUseCase(this.samContext, this.checkoutConfig).execute(params).pipe(tap(response => this.cart$.next(response)), tap(() => this.cartChanges$$.next({
        action: 'add',
        type: 'cart',
        quantity: productQuantityAdded,
        product
      })), catchError(() => this.emptyCart$));
    }
    updateCart$(product) {
      const products = this.getProducts();
      return UpdateCartUseCase(this.samContext, this.checkoutConfig).execute({
        products,
        product,
        ...this.cartData
      })?.pipe(tap(response => this.cart$.next(response)), tap(() => this.cartChanges$$.next({
        action: 'update',
        type: 'cart',
        quantity: product.quantityChanged ? product.quantityChanged : 0,
        product
      })), catchError(() => this.emptyCart$));
    }
    removeFromCart$(product) {
      const products = this.getProducts();
      if (!product || !products) return this.emptyCart$;
      return RemoveFromCartUseCase(this.samContext, this.checkoutConfig).execute({
        products,
        productId: product.productId,
        ...this.cartData
      })?.pipe(tap(response => this.cart$.next(response)), tap(() => this.cartChanges$$.next({
        action: 'remove',
        type: 'cart',
        quantity: product.quantityChanged ? product.quantityChanged : product.quantity,
        product: product
      })), catchError(() => this.emptyCart$));
    }
    deleteCart$() {
      return DeleteCartUseCase(this.samContext, this.checkoutConfig).execute(this.cartData)?.pipe(tap(response => this.cart$.next(response)), catchError(() => this.emptyCart$));
    }
    productsTillCartFull() {
      return this.getCount$().pipe(map(count => this.maxOrderQuantity - count));
    }
    static {
      this.ɵfac = function CartService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CartService)(i0.ɵɵinject(SamAngularService), i0.ɵɵinject(i1.CheckoutConfigService), i0.ɵɵinject(i1.StoreConfigService), i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(LocalStorageService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CartService,
        factory: CartService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CartService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let StoreSearchService = /*#__PURE__*/(() => {
  class StoreSearchService {
    constructor(filtersConfigService, samAngularService, storeSearchConfigService, legacyConfigService) {
      this.filtersConfigService = filtersConfigService;
      this.samAngularService = samAngularService;
      this.storeSearchConfigService = storeSearchConfigService;
      this.legacyConfigService = legacyConfigService;
      this.offset = CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_OFFSET;
      this.pageSize = CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_SIZE;
      this.filtersSubject = new Subject();
      this.filterChanges$$ = new ReplaySubject();
      this.filterSelected = {
        offset: this.offset,
        pageSize: this.pageSize,
        filters: []
      };
      this.samContext = this.samAngularService.getContext();
      this.filtersConfiguration = JSON.parse(JSON.stringify(this.filtersConfigService.get().filtersList));
      this.storeSearchConfiguration = this.storeSearchConfigService.get();
      this.legacyConfig = this.legacyConfigService.get();
      this.colorReferences = this.legacyConfig.metacolorReferences;
    }
    getFiltersObservable$() {
      return this.filtersSubject.asObservable();
    }
    getStoreSearchParams() {
      return this.storeSearchConfiguration;
    }
    loadFiltersCriteria() {
      return JSON.parse(JSON.stringify(this.filtersConfiguration));
    }
    getFilterSelected() {
      return this.filterSelected;
    }
    setFilters(newFilters) {
      const currentFilters = this.filterSelected;
      if (newFilters.offset || newFilters.offset === 0) {
        currentFilters.offset = newFilters.offset;
      }
      if (newFilters.pageSize) {
        currentFilters.pageSize = newFilters.pageSize;
      }
      if (!newFilters.filters) {
        this.filtersSubject.next(currentFilters);
        return;
      }
      if (newFilters.filters.length === 0) {
        currentFilters.filters = newFilters.filters;
        this.filtersSubject.next(currentFilters);
        return;
      }
      newFilters.filters?.forEach(newFilter => {
        const currentFilterIndex = currentFilters.filters.findIndex(filter => filter.name === newFilter.name);
        if (currentFilterIndex < 0) {
          currentFilters.filters.push(newFilter);
          return;
        }
        if (newFilter.values.length > 0) {
          currentFilters.filters[currentFilterIndex].values = newFilter.values;
          return;
        }
        currentFilters.filters.splice(currentFilterIndex, 1);
      });
      this.filtersSubject.next(currentFilters);
    }
    getProductsByFilters(params, action) {
      const queryParams = params.filters || params.search || params.crit;
      return GetProductsByFiltersUseCase(this.samContext).execute({
        params,
        metaproductColors: this.colorReferences ?? {}
      }).pipe(tap$1(filteredProducts => this.filterChanges$$.next({
        action,
        type: 'productsDisplay',
        totalQuantity: filteredProducts.total,
        products: filteredProducts.products.map((element, index) => {
          return {
            ...element,
            position: index + 1
          };
        }),
        queryParams,
        pagination: {
          count: params.count,
          offset: params.offset
        }
      })));
    }
    getDefaultFiltersFromFiltersConfiguration() {
      const defaultFilters = [];
      this.filtersConfiguration.forEach(filter => {
        const filterValues = filter.options.filter(option => option.checked).map(filter => filter.value);
        if (filterValues.length > 0) {
          defaultFilters.push({
            name: filter.value,
            values: filterValues,
            groupBy: filter.groupBy
          });
        }
      });
      return defaultFilters;
    }
    updateFiltersConfiguration(filters) {
      this.filtersConfiguration.forEach(filter => {
        const matchingFilter = filters.filters.find(f => f.name === filter.value);
        if (matchingFilter) {
          const filterValues = matchingFilter.values ?? [];
          filter.options.forEach(option => {
            const isChecked = filterValues.some(value => value.split(',').map(v => v.trim()).includes(option.value));
            option.checked = isChecked;
          });
        }
      });
    }
    resetFilters() {
      return JSON.parse(JSON.stringify(this.filtersConfigService.get().filtersList));
    }
    createFilterTag(tagList, filter) {
      const filterTags = {
        name: filter.value,
        values: tagList.map(item => {
          return {
            label: item.label,
            value: item.value
          };
        }),
        groupBy: filter.groupBy
      };
      return filterTags;
    }
    updateFilterChangedInLoadCriterial(filter, filtersCriteria) {
      const {
        name,
        values
      } = filter;
      const filtersClone = JSON.parse(JSON.stringify(filtersCriteria));
      filtersClone.filter(filterConfig => filterConfig.value === name).forEach(filterConfig => {
        const selectedValues = new Set(values.map(value => value[0]));
        filterConfig.options.forEach(option => {
          if (!selectedValues.has(option.value)) {
            option.checked = false;
          }
        });
      });
      return filtersClone;
    }
    removeExcludedFilters(filtersCriteria, categoryId) {
      return filtersCriteria.filter(filterCriteria => !filterCriteria.excludedCategories.includes(parseInt(categoryId)));
    }
    /**
     * Set the action for the event 'productDisplayed' used by the analyticsService.
     * It distingish the action from the first charge ('load') or the filtered products ('filter'),
     * and also both of them with pagination ('load-more' and 'filter-more' respectively).
     * @returns
     */
    setEventProductDisplayedAction(filters) {
      if (filters.filters.length > 0) {
        if (filters.offset === 0) {
          return 'filter';
        } else {
          return 'filter-more';
        }
      } else {
        if (filters.offset === 0) {
          return 'load';
        } else {
          return 'load-more';
        }
      }
    }
    static {
      this.ɵfac = function StoreSearchService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StoreSearchService)(i0.ɵɵinject(i1.FiltersConfigService), i0.ɵɵinject(SamAngularService), i0.ɵɵinject(i1.StoreSearchConfigService), i0.ɵɵinject(i1.LegacyConfigService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StoreSearchService,
        factory: StoreSearchService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return StoreSearchService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CategoryPageService = /*#__PURE__*/(() => {
  class CategoryPageService extends StoreSearchService {
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵCategoryPageService_BaseFactory;
        return function CategoryPageService_Factory(__ngFactoryType__) {
          return (ɵCategoryPageService_BaseFactory || (ɵCategoryPageService_BaseFactory = i0.ɵɵgetInheritedFactory(CategoryPageService)))(__ngFactoryType__ || CategoryPageService);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CategoryPageService,
        factory: CategoryPageService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CategoryPageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SearchResultsPageService = /*#__PURE__*/(() => {
  class SearchResultsPageService extends StoreSearchService {
    static {
      this.ɵfac = /* @__PURE__ */(() => {
        let ɵSearchResultsPageService_BaseFactory;
        return function SearchResultsPageService_Factory(__ngFactoryType__) {
          return (ɵSearchResultsPageService_BaseFactory || (ɵSearchResultsPageService_BaseFactory = i0.ɵɵgetInheritedFactory(SearchResultsPageService)))(__ngFactoryType__ || SearchResultsPageService);
        };
      })();
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SearchResultsPageService,
        factory: SearchResultsPageService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SearchResultsPageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const METAPRODUCT_SELECTOR_SERVICE = new InjectionToken('METAPRODUCT_SELECTOR_SERVICE_TOKEN');
const PAGE_DATA = new InjectionToken('PAGE_DATA');
const PRODUCT_SLUGGER_SERVICE = new InjectionToken('PRODUCT_SLUGGER_SERVICE_TOKEN');
const sluggerTestDouble = {
  slugify: () => 'slug',
  getRouterLink: () => ['product', '0', 'slug']
};

/**
 * PrefetchHandler interface receives a generic type, that's why type here with any.
 * TODO: The any type may be problematic. We should define a more specific interface.
 */
const PREFETCH_SERVICES = new InjectionToken('PREFETCH_SERVICES');
const IS_BUILDER_EDITOR = new InjectionToken('IS_BUILDER_EDITOR');
const PAGE_RESOLVERS = new InjectionToken('PAGE_RESOLVERS');
class Logger {
  constructor(logger) {
    this.logger = logger;
  }
  fatal(msg, ...args) {
    this.logger.fatal(msg, ...args);
  }
  error(msg, ...args) {
    this.logger.error(msg, ...args);
  }
  warn(msg, ...args) {
    this.logger.warn(msg, ...args);
  }
  info(msg, ...args) {
    this.logger.info(msg, ...args);
  }
  debug(msg, ...args) {
    this.logger.debug(msg, ...args);
  }
  trace(msg, ...args) {
    this.logger.debug(msg, ...args);
  }
  /**
   * Updates the log level of the this.logger.
   * @param logLevel - The new log level to set.
   */
  updateLogLevel(logLevel) {
    // this.logger.updateLogLevel(logLevel);
  }
}

/**
 * Browser logger service manages the logger in the local browser console.
 * It can't be controlled dynamically through the server and env variables since the code is static so
 * it checks `pandoraLogLevel` in the globalThis object and localStorage to set the level.
 * It defaults to the `browserLogLevel` in the store config and often varies by environment.
 */
let BrowserLoggerService = /*#__PURE__*/(() => {
  class BrowserLoggerService {
    #rootLogger;
    #globalThis = globalThis; // This allows arbitrary properties to be set in the globalThis object
    constructor(storeConfigService) {
      this.storeConfigService = storeConfigService;
      if (!this.#globalThis.pandoraLogLevel) {
        return;
      }
      let storageLevel;
      if (typeof localStorage !== 'undefined') {
        storageLevel = localStorage.getItem('pandoraLogLevel');
      }
      const configLevel = this.storeConfigService.get().browserLogLevel;
      this.#globalThis.pandoraLogLevel = storageLevel ?? configLevel ?? 'info';
    }
    #getLevel() {
      const pandoraLogLevel = this.#globalThis.pandoraLogLevel;
      return isValidLogLevel(pandoraLogLevel) ? pandoraLogLevel : 'info';
    }
    getLogger() {
      if (!this.#rootLogger) {
        this.#rootLogger = new BrowserMapper({}, () => this.#getLevel());
        this.#rootLogger.debug('Root Browser logger initialized');
      }
      return this.#rootLogger;
    }
    static {
      this.ɵfac = function BrowserLoggerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BrowserLoggerService)(i0.ɵɵinject(i1.StoreConfigService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BrowserLoggerService,
        factory: BrowserLoggerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BrowserLoggerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class BrowserMapper {
  /**
   * Keeps track of the logger inherited properties for Pino compatibility
   */
  #childObj = {};
  /**
   * @param defaultLogLevel This log level will be used if the browser doesn't have
   * set a globalThis.storeLogLevel level
   */
  constructor(childObject, getLevel) {
    this.getLevel = getLevel;
    this.#childObj = childObject;
  }
  child(childObj) {
    const fullChild = Object.assign({}, this.#childObj, childObj);
    return new BrowserMapper(fullChild, this.getLevel);
  }
  hasToPrint(logLevel) {
    return logLevelValueMapping[logLevel] <= logLevelValueMapping[this.getLevel()];
  }
  fatal(...args) {
    if (this.hasToPrint('fatal')) {
      console.error('fatal', this.#childObj, ...args);
    }
  }
  error(...args) {
    if (this.hasToPrint('error')) {
      console.error(this.#childObj, ...args);
    }
  }
  warn(...args) {
    if (this.hasToPrint('warn')) {
      console.warn(this.#childObj, ...args);
    }
  }
  info(...args) {
    if (this.hasToPrint('info')) {
      console.info(this.#childObj, ...args);
    }
  }
  debug(...args) {
    if (this.hasToPrint('debug')) {
      console.debug(this.#childObj, ...args);
    }
  }
  trace(...args) {
    if (this.hasToPrint('trace')) {
      console.trace(this.#childObj, ...args);
    }
  }
}

/**
 * The server logger service wraps the SERVER_LOGGER provided by the SSR server to be compatible with
 * LoggerService, it's instantiated outside the DI system to handle SERVER_LOGGER being optional
 */
class ServerLoggerService {
  #serverLoggerInstance;
  constructor(serverLogger) {
    this.#serverLoggerInstance = serverLogger.child({
      from: 'AngularRender'
    });
  }
  getLogger() {
    return this.#serverLoggerInstance;
  }
  child(childObj) {
    return this.#serverLoggerInstance.child(childObj);
  }
}

/**
 * This service is used to provide a universal logger that works in both the server and the browser
 * The browser logger is a wrapper over console
 */
let UniversalLoggerService = /*#__PURE__*/(() => {
  class UniversalLoggerService {
    constructor() {
      const platformId = inject(PLATFORM_ID);
      if (isPlatformBrowser(platformId)) {
        this.loggerService = inject(BrowserLoggerService);
        return;
      }
      const serverLogger = inject(SERVER_LOGGER, {
        optional: true
      });
      const serverProvidersRequired = inject(SERVER_PROVIDERS_REQUIRED, {
        optional: true
      });
      if (!serverLogger && serverProvidersRequired) {
        throw new Error('SERVER_LOGGER inject token not available and required');
      }
      if (serverLogger) {
        this.loggerService = new ServerLoggerService(serverLogger);
      } else {
        // When running the dev-server we don't have the server logger available so we fake it with the browser logger
        // This could be done more cleanly but Angular may remove this limitation
        // https://github.com/angular/angular-cli/issues/26323
        this.loggerService = inject(BrowserLoggerService);
      }
    }
    static {
      this.ɵfac = function UniversalLoggerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UniversalLoggerService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UniversalLoggerService,
        factory: UniversalLoggerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UniversalLoggerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductLogger = /*#__PURE__*/(() => {
  class ProductLogger extends Logger {
    constructor() {
      const universalLoggerService = inject(UniversalLoggerService);
      super(universalLoggerService.loggerService.getLogger().child({
        module: 'product'
      }));
    }
    static {
      this.ɵfac = function ProductLogger_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductLogger)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductLogger,
        factory: ProductLogger.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductLogger;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductService = /*#__PURE__*/(() => {
  class ProductService {
    constructor(samAngularService, legacyConfigService, samConfigService, localizationConfigService, productLogger) {
      this.samAngularService = samAngularService;
      this.legacyConfigService = legacyConfigService;
      this.samConfigService = samConfigService;
      this.localizationConfigService = localizationConfigService;
      this.productLogger = productLogger;
      // FIXME: Not the best cache, but should improve performance in redirect and other use cases
      this.productsObservablesMap = new Map();
      this.productsRelatedObservablesMap = new Map();
      this.productsYMALObservablesMap = new Map();
      this.productsByCriteriaObservablesMap = new Map();
      this.relatedProducts$$ = new ReplaySubject();
      this.samContext = this.samAngularService.getContext();
      this.samConfig = this.samConfigService.get();
      this.legacyConfig = this.legacyConfigService.get();
      this.localizationConfig = this.localizationConfigService.get();
      this.clientId = this.samContext.apiContext.v4.clientId;
      this.colorReferences = this.legacyConfig.metacolorReferences;
    }
    getProductNoCache$(productId) {
      return GetProductWithMetaproductsUseCase(this.samContext).execute({
        productID: productId,
        territory: this.localizationConfig.currentLang,
        projectID: this.samConfig.apiContext.v4.projectId,
        userID: this.samConfig.apiContext.v4.userID,
        metaproductColors: this.colorReferences ?? {}
      }).pipe(tap(product => this.productLogger.debug('ProductService.getProduct$ request', {
        productId
      })));
    }
    getProduct$(productId) {
      if (typeof productId === 'string') {
        productId = parseInt(productId, 10);
        this.productLogger.warn(`ProductService.getProduct$ ${productId} called with string productId. It should be a number.`);
      }
      if (!this.productsObservablesMap.has(productId)) {
        this.productsObservablesMap.set(productId, this.getProductNoCache$(productId).pipe(shareReplay(1)));
        this.productLogger.debug('ProductService.getProduct$ cache miss', {
          productId
        });
      } else {
        this.productLogger.debug('ProductService.getProduct$ cache hit', {
          productId
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const product = this.productsObservablesMap.get(productId); // Dumb ts. Checked map is not undefined above
      // FIXME Clone object as we can't afford trusting immutability now, but Product should be readonly
      return product.pipe(map(product => JSON.parse(JSON.stringify(product))));
    }
    getProductBySkuNoCache$(refId) {
      return GetProductBySkuUseCase(this.samContext).execute({
        sku: refId,
        territory: this.localizationConfig.currentLang,
        projectID: this.samConfig.apiContext.v4.projectId,
        userID: this.samConfig.apiContext.v4.userID
      }).pipe(tap(product => this.productLogger.info('ProductService.getProductBySku$ request', {
        refId
      })));
    }
    /** @deprecated this method is using the v4 public store token. It should be using Store Search */
    getRelatedProductsNoCache$(product) {
      if (!product.relatedProducts) {
        return of([]);
      }
      const params = {
        relatedProductReference: product.relatedProducts,
        clientID: this.clientId,
        userID: this.samConfig.apiContext.v4.userID,
        limit: 4,
        territory: this.localizationConfig.territories.find(() => true) || 'REU',
        metaproductColors: this.colorReferences ?? {}
      };
      return GetRelatedProductsUseCase(this.samContext).execute(params).pipe(tap(products => this.productLogger.debug('ProductService.getRelatedProducts$ request', {
        product,
        products
      })));
    }
    getProductByEanNoCache$(ean) {
      const query = {
        fields: {
          ean
        },
        currency: this.localizationConfig.currency
      };
      return GetProductsByCriteriaUseCase(this.samContext).execute(query).pipe(map(products => {
        if (products.length > 1) {
          this.productLogger.warn('ProductService.getProductByEan$ multiple products found for ean', {
            ean,
            products
          });
        }
        if (!products[0]) {
          throw new Error(`ProductService.getProductByEan$ no product found for ean ${ean}`);
        }
        return products[0];
      }), tap(product => this.productLogger.debug('ProductService.getProductByEan$ request', query, product)));
    }
    getProductByEan$(ean) {
      const eanKey = `ean-${ean}`;
      if (!this.productsByCriteriaObservablesMap.has(eanKey)) {
        this.productsByCriteriaObservablesMap.set(eanKey, this.getProductByEanNoCache$(ean).pipe(shareReplay(1)));
        this.productLogger.debug('ProductService.getProductByEan$ cache miss', {
          ean
        });
      } else {
        this.productLogger.debug('ProductService.getProductByEan$ cache hit', {
          ean
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const product = this.productsByCriteriaObservablesMap.get(eanKey); // Dumb ts. Checked map is not undefined above
      // FIXME Clone object as we can't afford trusting immutability now, but Product should be readonly
      return product.pipe(map(product => JSON.parse(JSON.stringify(product))));
    }
    /** @deprecated this method is using the v4 public store token. It should be using Store Search */
    getRelatedProducts$(mainProduct) {
      const productId = parseInt(mainProduct.id);
      if (!this.productsRelatedObservablesMap.has(productId)) {
        this.productsRelatedObservablesMap.set(productId, this.getRelatedProductsNoCache$(mainProduct).pipe(shareReplay(1)));
        this.productLogger.debug('ProductService.getRelatedProducts$ cache miss', {
          productId
        });
      } else {
        this.productLogger.debug('ProductService.getRelatedProducts$ cache hit', {
          productId
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const product = this.productsRelatedObservablesMap.get(productId); // Dumb ts. Checked map is not undefined above
      // FIXME Clone object as we can't afford trusting immutability now, but Product should be readonly
      return product.pipe(tap(product => {
        if (product.length) {
          this.relatedProducts$$.next({
            action: 'show-related',
            type: 'carousel',
            quantity: product.length,
            products: product.map((element, index) => {
              return {
                ...element,
                position: index + 1
              };
            })
          });
        }
      }), map(products => JSON.parse(JSON.stringify(products))));
    }
    /** @deprecated this method is using the v4 public store token. It should be using Store Search */
    getYouMayAlsoLikeProductsNoCache$(product) {
      if (!product.recommendedProducts) {
        return of([]);
      }
      const params = {
        relatedProductReference: product.recommendedProducts,
        clientID: this.clientId,
        userID: this.samConfig.apiContext.v4.userID,
        limit: 4,
        territory: this.localizationConfig.territories.find(() => true) || 'REU',
        metaproductColors: this.colorReferences ?? {}
      };
      return GetRelatedProductsUseCase(this.samContext).execute(params).pipe(tap(products => {
        this.productLogger.debug('ProductService.getYouMayAlsoLikeProducts$', {
          product,
          products
        });
        if (products.length) {
          this.relatedProducts$$.next({
            action: 'show-may-also-like',
            type: 'carousel',
            quantity: products.length,
            products: products.map((element, index) => {
              return {
                ...element,
                position: index + 1
              };
            })
          });
        }
      }));
    }
    getYouMayAlsoLikeProducts$(mainProduct) {
      const productId = parseInt(mainProduct.id);
      if (!this.productsYMALObservablesMap.has(productId)) {
        this.productsYMALObservablesMap.set(productId, this.getYouMayAlsoLikeProductsNoCache$(mainProduct).pipe(shareReplay(1)));
        this.productLogger.debug('ProductService.getYouMayAlsoLikeProducts$ cache miss', {
          productId
        });
      } else {
        this.productLogger.debug('ProductService.getYouMayAlsoLikeProducts$ cache hit', {
          productId
        });
      }
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const product = this.productsYMALObservablesMap.get(productId); // Dumb ts. Checked map is not undefined above
      // FIXME Clone object as we can't afford trusting immutability now, but Product should be readonly
      return product.pipe(map(product => JSON.parse(JSON.stringify(product))));
    }
    /**
     * @deprecated this method is using the v4 api. It should be using Store Search
     */
    getProductsByCategory(categoryId, limit, offset) {
      return GetProductsByCategoryUseCase(this.samContext).execute({
        categoryId,
        clientId: this.clientId,
        limit,
        offset
      }).pipe(tap(products => this.productLogger.debug('ProductService.getProductsByCategory', {
        categoryId,
        limit,
        offset,
        products
      })));
    }
    static {
      this.ɵfac = function ProductService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductService)(i0.ɵɵinject(SamAngularService), i0.ɵɵinject(i1.LegacyConfigService), i0.ɵɵinject(i1.SamConfigService), i0.ɵɵinject(i1.LocalizationConfigService), i0.ɵɵinject(ProductLogger));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductService,
        factory: ProductService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductBreadcrumbService = /*#__PURE__*/(() => {
  class ProductBreadcrumbService {
    constructor(locale, router) {
      this.locale = locale;
      this.router = router;
      this._breadcrumbs$ = new BehaviorSubject([]);
      this.breadcrumbs$ = this._breadcrumbs$.asObservable();
      this.breadcrumbs = [];
      this.productBreadcrumbs = [];
      const root = this.router.routerState.snapshot.root;
      this.updateBreadcrumb(root); // The breadcrumb is updated on page reload
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        const root = this.router.routerState.snapshot.root;
        this.updateBreadcrumb(root);
      });
    }
    setProductBreadcrumbs(product) {
      this.productBreadcrumbs = this.generateCategoryPath(product.categories);
      this.addProductTitle(product);
    }
    generateCategoryPath(productCategories) {
      const categoriesBreadcrumbs = [];
      productCategories.forEach((category, index) => {
        const categoryLabelSanitised = this.generateProductCategoryPath(category.categoryName);
        const path = categoriesBreadcrumbs[index - 1] ? `${categoriesBreadcrumbs[index - 1].path}/${categoryLabelSanitised}` : `/categories/${categoryLabelSanitised}`;
        categoriesBreadcrumbs.push({
          label: category.categoryName,
          path: path
        });
      });
      return categoriesBreadcrumbs;
    }
    // Replace all special characters to generate clean url
    generateProductCategoryPath(categoryName) {
      categoryName = categoryName.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return categoryName.toLowerCase().replace(/ /g, '-');
    }
    addProductBreadcrumbs() {
      this.breadcrumbs = this.productBreadcrumbs.concat(this.breadcrumbs);
      this.productBreadcrumbs = [];
    }
    addProductTitle(product) {
      // FIXME: find a better way to map angular locale to API language
      // https://setnext.scalefast.ninja/scalefast/frontend/pandora/-/issues/627
      const lang = this.locale.split('-')[0].toUpperCase();
      const description = product.descriptions.find(item => item.lang === lang);
      const productTitle = description ? description.title : product.descriptions[0].title;
      this.productBreadcrumbs.push({
        label: productTitle,
        path: ''
      });
    }
    updateBreadcrumb(root) {
      this.generateBreadcrumb(root);
      this.addProductBreadcrumbs();
      this._breadcrumbs$.next(this.breadcrumbs);
    }
    /**
     * Generate the breadcrumb from the route
     * @param route ActivatedRouteSnapshot
     * @returns void
     */
    generateBreadcrumb(route) {
      this.breadcrumbs = [];
      let url = undefined;
      // route.firstChild is the first child of the root route (e.g. /home)
      while (route.firstChild) {
        if (route.firstChild.routeConfig?.path) {
          url = this.getUrl(route.firstChild, url);
        }
        route = route.firstChild;
      }
      // Get the breadcrumb data from the route
      const {
        breadcrumb
      } = route.data ?? undefined;
      // Escape if there is no breadcrumb data or the breadcrumb is set to '**'
      if (!url || url === '**' || !breadcrumb) {
        return;
      }
      const breadcrumbData = breadcrumb ? this.getLabel(route.data) : undefined;
      // Sanitize the breadcrumb
      this.sanitizeBreadcrumb();
    }
    /**
     * Get the URL from the route and replace the parameters with the actual values
     * @param route ActivatedRouteSnapshot
     * @param path string
     * @returns string
     */
    getUrl(route, path) {
      let url = route.routeConfig?.path;
      if (route.params) {
        Object.keys(route.params).forEach(key => {
          url = url?.replace(`${key}`, route.params[key]);
        });
      }
      return path ? `${path}/${url}` : url;
    }
    /**
     * Get the label from the route data
     * @param data Data
     * @returns string
     */
    getLabel(data) {
      return typeof data.breadcrumb === 'function' ? data.breadcrumb(data) : data.breadcrumb;
    }
    /**
     * Sanitize the breadcrumb
     */
    sanitizeBreadcrumb() {
      this.removeOrphanedBreadcrumb();
      this.removeColonsFromBreadcrumb();
    }
    /**
     * Remove breadcrumb that doesn't make sense without the parameter that follows it
     */
    removeOrphanedBreadcrumb() {
      const index = this.breadcrumbs.findIndex(item => item.path.includes(':')) - 1;
      if (index > -1) {
        this.breadcrumbs.splice(index, 1);
      }
    }
    /**
     * Remove the colons from the breadcrumb label and path if are present
     */
    removeColonsFromBreadcrumb() {
      this.breadcrumbs = this.breadcrumbs.map(item => {
        return {
          label: item.label.replace(/:/g, ''),
          path: item.path.replace(/:/g, '')
        };
      });
    }
    static {
      this.ɵfac = function ProductBreadcrumbService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductBreadcrumbService)(i0.ɵɵinject(LOCALE_ID), i0.ɵɵinject(i1$1.Router));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductBreadcrumbService,
        factory: ProductBreadcrumbService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductBreadcrumbService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductSluggerService = /*#__PURE__*/(() => {
  class ProductSluggerService {
    constructor(locale) {
      this.locale = locale;
    }
    slugify(product) {
      return getProductCanonicalSlug(product, this.locale);
    }
    getRouterLink(product) {
      return ['/product', product.id, this.slugify(product)];
    }
    static {
      this.ɵfac = function ProductSluggerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductSluggerService)(i0.ɵɵinject(LOCALE_ID));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductSluggerService,
        factory: ProductSluggerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductSluggerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductResolverService = /*#__PURE__*/(() => {
  class ProductResolverService {
    constructor(productService, productBreadcrumbService, router, destroyRef, logger, sluggerService, metaproductSelectorService) {
      this.productService = productService;
      this.productBreadcrumbService = productBreadcrumbService;
      this.router = router;
      this.destroyRef = destroyRef;
      this.logger = logger;
      this.sluggerService = sluggerService;
      this.metaproductSelectorService = metaproductSelectorService;
      this.productPageDisplayed$$ = new ReplaySubject();
    }
    setBreadcrumbsData(product$) {
      product$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(product => {
        this.productBreadcrumbService.setProductBreadcrumbs(product);
      });
    }
    generateProductPageDisplayedEvent(product$) {
      product$.subscribe(product => this.productPageDisplayed$$.next({
        action: 'show',
        type: 'productPageDisplayed',
        product
      }));
    }
    /**
     * Redirects to the first available child in case the product is a meta product.
     * @param product$ Observable<Product>
     * @returns void
     */
    selectChildMetaproduct(product$) {
      product$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(product => {
        if (product.type === 'META') {
          try {
            const productId = this.metaproductSelectorService.productSelector(product);
            this.router.navigate(['/product', productId]); // FIXME Use sluggerservice for this link (maybe not possible due to lack of data)
          } catch (err) {
            this.router.navigateByUrl('/not-found', {
              onSameUrlNavigation: 'reload',
              skipLocationChange: true
            });
            this.logger.error(err);
          }
        }
      });
    }
    /**
     * Show not found page without changing the url, we are not sure if the resolver is the best place for this
     * but has worked for us so far.
     */
    async goToNotFound() {
      return this.router.navigateByUrl('/not-found', {
        onSameUrlNavigation: 'reload',
        skipLocationChange: true
      });
    }
    /**
     * TODO: This function is requesting a product and handling
     * missing products error, it should be split
     **/
    getProduct$(id) {
      return this.productService.getProduct$(id).pipe(take(1), catchError(err => {
        this.goToNotFound();
        this.logger.error(err);
        return EMPTY;
      }), share());
    }
    /**
     * If the product slug doesn't match the expected value rewrites the url with the correct one
     */
    fixSlug(product$, currentSlug) {
      product$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(product => {
        const slug = this.sluggerService.slugify(product);
        if (slug !== currentSlug) {
          this.router.navigate(this.sluggerService.getRouterLink(product));
        }
      });
    }
    /**
     * Checks if the product state is not valid, since V4 API can return Invisible products.
     */
    checkProductStateValid(product$) {
      product$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(product => {
        if (product.state === 'INVISIBLE') {
          this.goToNotFound();
          this.logger.warn(`Trying to access Invisible product: ${product.id}`);
        }
      });
    }
    refIdRedirect(refId) {
      const product$ = this.productService.getProductBySkuNoCache$(refId).pipe(takeUntilDestroyed(this.destroyRef), catchError(err => {
        this.goToNotFound();
        this.logger.error(err);
        return EMPTY;
      }));
      product$.subscribe(product => {
        this.router.navigate(this.sluggerService.getRouterLink(product));
      });
      return product$;
    }
    eanRedirect(ean) {
      const product$ = this.productService.getProductByEanNoCache$(ean).pipe(takeUntilDestroyed(this.destroyRef), catchError(err => {
        this.goToNotFound();
        this.logger.error(err);
        return EMPTY;
      }));
      product$.subscribe(product => {
        this.router.navigate(this.sluggerService.getRouterLink(product));
        return product;
      });
      return product$;
    }
    resolve(route) {
      if (route?.routeConfig?.path === 'products') {
        //FIXME We should probably have a different resolver for this
        const refId = route.queryParamMap.get('refid');
        if (refId) {
          return this.refIdRedirect(refId);
        }
        const ean = route.queryParamMap.get('ean');
        if (ean) {
          return this.eanRedirect(ean);
        }
        this.goToNotFound();
        this.logger.error(`The refId/ean not provided`);
        return EMPTY;
      }
      const id = parseInt(route.params['productId']);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const slug = route.params['productSlug']; // Not yet used for anything, but we may want to check/redirect here
      const product$ = this.getProduct$(id);
      this.checkProductStateValid(product$);
      this.fixSlug(product$, slug);
      // TODO: https://setnext.scalefast.ninja/scalefast/frontend/pandora/-/issues/534
      this.selectChildMetaproduct(product$);
      this.generateProductPageDisplayedEvent(product$);
      this.setBreadcrumbsData(product$);
      return product$;
    }
    static {
      this.ɵfac = function ProductResolverService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductResolverService)(i0.ɵɵinject(ProductService), i0.ɵɵinject(ProductBreadcrumbService), i0.ɵɵinject(i1$1.Router), i0.ɵɵinject(i0.DestroyRef), i0.ɵɵinject(ProductLogger), i0.ɵɵinject(PRODUCT_SLUGGER_SERVICE), i0.ɵɵinject(METAPRODUCT_SELECTOR_SERVICE));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductResolverService,
        factory: ProductResolverService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductResolverService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductCarouselService = /*#__PURE__*/(() => {
  class ProductCarouselService {
    constructor(samAngularService, productService, storeSearchConfigService, storeConfigService, legacyConfigService) {
      this.samAngularService = samAngularService;
      this.productService = productService;
      this.storeSearchConfigService = storeSearchConfigService;
      this.storeConfigService = storeConfigService;
      this.legacyConfigService = legacyConfigService;
      this.homeModuleProducts$ = new ReplaySubject();
      this.modulesWithProductsByFeedId$ = EMPTY;
      this.samContext = this.samAngularService.getContext();
      this.moduleConfig = storeConfigService.getHomeModuleCarousel();
      this.legacyConfig = this.legacyConfigService.get();
      this.colorReferences = this.legacyConfig.metacolorReferences;
    }
    getProducts$(productId) {
      if (!productId) {
        return;
      }
      const product$ = this.productService.getProduct$(parseInt(productId));
      return product$?.pipe(switchMap(product => this.productService.getYouMayAlsoLikeProducts$(product)));
    }
    getFeedProductsEvent(feedId) {
      return this.getFeedProducts$(feedId).pipe(tap(productsByFeedId =>
      //Event for analitics
      this.homeModuleProducts$.next({
        action: 'show',
        type: 'carousel',
        quantity: productsByFeedId.total,
        products: productsByFeedId.products.map((element, index) => {
          return {
            ...element,
            position: index + 1
          };
        })
      })));
    }
    getFeedProducts$(feedId) {
      if (this.moduleConfig) {
        const params = {
          ...this.storeSearchConfigService.get(),
          count: this.moduleConfig.carouselLimit,
          offset: CATEGORY_FILTERS_OPTIONS.DEFAULT_PAGE_OFFSET,
          crit: `feedID:${feedId}`
        };
        return GetProductsByFeedUseCase(this.samContext).execute({
          params,
          metaproductColors: this.colorReferences ?? {}
        })?.pipe(catchError(error => {
          console.log('error', error);
          return of({
            total: 0,
            products: []
          });
        }));
      }
      return of({
        total: 0,
        products: []
      });
    }
    static {
      this.ɵfac = function ProductCarouselService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductCarouselService)(i0.ɵɵinject(SamAngularService), i0.ɵɵinject(ProductService), i0.ɵɵinject(i1.StoreSearchConfigService), i0.ɵɵinject(i1.StoreConfigService), i0.ɵɵinject(i1.LegacyConfigService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductCarouselService,
        factory: ProductCarouselService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductCarouselService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AnalyticsService = /*#__PURE__*/(() => {
  class AnalyticsService {
    constructor(cartService, storeSearchService, categoryPageService, searchResultsPageService, productResolverService, productCarouselService, productService) {
      this.cartService = cartService;
      this.storeSearchService = storeSearchService;
      this.categoryPageService = categoryPageService;
      this.searchResultsPageService = searchResultsPageService;
      this.productResolverService = productResolverService;
      this.productCarouselService = productCarouselService;
      this.productService = productService;
      this.events$ = merge(this.getCartChanges$(), this.getFilteredProducts$(), this.getCategoryPageFilteredProducts$(), this.getSearchResultsPageFilteredProducts$(), this.getProductPageDisplayed$(), this.getProductCarousel$());
    }
    /**
     * Get all changes in the cart when adding, removing or updating a product.
     * @returns observable of the event with the action ('add' | 'remove' | 'update'),
     * the type (cart), the quantity and the value changed (product information)
     */
    getCartChanges$() {
      return this.cartService.cartChanges$$.asObservable();
    }
    /**
     * Get all the products in the grid when charged and after filtering
     * @returns observable of the event with the action (filter),
     * the type (filter), the total quantity of products and the visible products
     */
    getFilteredProducts$() {
      return this.storeSearchService.filterChanges$$.asObservable();
    }
    /**
     * Get all the products in the category page grid when charged and after filtering
     * @returns observable of the event with the action (filter),
     * the type (filter), the total quantity of products and the visible products
     */
    getCategoryPageFilteredProducts$() {
      return this.categoryPageService.filterChanges$$.asObservable();
    }
    /**
     * Get all the products in the search results page grid when charged and after filtering
     * @returns observable of the event with the action (filter),
     * the type (filter), the total quantity of products and the visible products
     */
    getSearchResultsPageFilteredProducts$() {
      return this.searchResultsPageService.filterChanges$$.asObservable();
    }
    /**
     * Get the event when a Product Page is displayed
     * @returns observable of the event with the action, the type and the product.
     */
    getProductPageDisplayed$() {
      return this.productResolverService.productPageDisplayed$$.asObservable();
    }
    /**
     * Get the products in the carousel from the homeModule
     * TODO: the carousel event is retrieved from the homeModuleService. The event should come from the CarouselService.
     * @returns observable of the event with the action, the type, the quantity and the products
     */
    getProductCarousel$() {
      const carouselProducts = merge(this.productService.relatedProducts$$.asObservable(), this.productCarouselService.homeModuleProducts$.asObservable());
      return carouselProducts;
    }
    static {
      this.ɵfac = function AnalyticsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AnalyticsService)(i0.ɵɵinject(CartService), i0.ɵɵinject(StoreSearchService), i0.ɵɵinject(CategoryPageService), i0.ɵɵinject(SearchResultsPageService), i0.ɵɵinject(ProductResolverService), i0.ɵɵinject(ProductCarouselService), i0.ɵɵinject(ProductService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AnalyticsService,
        factory: AnalyticsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AnalyticsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let AuthService = /*#__PURE__*/(() => {
  class AuthService {
    constructor(samAngularService, checkoutConfigService) {
      this.samAngularService = samAngularService;
      this.checkoutConfigService = checkoutConfigService;
      this.authCredentials$$ = new ReplaySubject();
      this.authCredentials$ = this.authCredentials$$.asObservable();
      this.authCredentialsStore$$ = new ReplaySubject();
      this.authCredentialsStore$ = this.authCredentialsStore$$.asObservable();
      this.platformId = inject(PLATFORM_ID);
      this.useCaseConfig = {
        implementation: 'v5'
      };
      this.samContext = this.samAngularService.getContext();
      this.checkoutConfig = this.checkoutConfigService.get();
      if (isPlatformBrowser(this.platformId)) {
        this.ΘgetStoreToken$().pipe(first(), takeUntilDestroyed()).subscribe();
        this.ΘgetM2MToken$().pipe(first(), takeUntilDestroyed()).subscribe();
      }
    }
    /**
     *
     * @experimental v5 auth is still WIP and this will be refactored when done
     */
    ΘgetStoreToken$() {
      return oAuthUseCase(this.samContext, this.useCaseConfig).execute({
        clientId: ''
      })?.pipe(tap(response => this.authCredentialsStore$$.next(response)));
    }
    /**
     *
     * @experimental v5 auth is still WIP and this will be refactored when done
     */
    ΘgetM2MToken$() {
      return M2MTokenUseCase(this.samContext, this.useCaseConfig).execute({
        username: this.checkoutConfig.username,
        password: this.checkoutConfig.password
      })?.pipe(tap(response => this.authCredentials$$.next(response)));
    }
    static {
      this.ɵfac = function AuthService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AuthService)(i0.ɵɵinject(SamAngularService), i0.ɵɵinject(i1.CheckoutConfigService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AuthService,
        factory: AuthService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AuthService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let BreadcrumbService = /*#__PURE__*/(() => {
  class BreadcrumbService {
    constructor(router) {
      this.router = router;
      this._breadcrumbs$ = new BehaviorSubject([]);
      this.breadcrumbs$ = this._breadcrumbs$.asObservable();
      this.breadcrumbs = [];
      const root = this.router.routerState.snapshot.root;
      this.updateBreadcrumb(root); // The breadcrumb is updated on page reload
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        const root = this.router.routerState.snapshot.root;
        this.updateBreadcrumb(root);
      });
    }
    updateBreadcrumb(root) {
      this.generateBreadcrumb(root);
      this._breadcrumbs$.next(this.breadcrumbs);
    }
    /**
     * Generate the breadcrumb from the route
     * @param route ActivatedRouteSnapshot
     * @returns void
     */
    generateBreadcrumb(route) {
      this.breadcrumbs = [];
      let url = undefined;
      // route.firstChild is the first child of the root route (e.g. /home)
      while (route.firstChild) {
        if (route.firstChild.routeConfig?.path) {
          url = this.getUrl(route.firstChild, url);
        }
        route = route.firstChild;
      }
      // Get the breadcrumb data from the route
      const {
        breadcrumb
      } = route.data ?? undefined;
      // Escape if there is no breadcrumb data or the breadcrumb is set to '**'
      if (!url || url === '**' || !breadcrumb) {
        return;
      }
      const urlSegments = url.split('/');
      const breadcrumbData = breadcrumb ? this.getLabel(route.data) : undefined;
      // Add the breadcrumbs to the array
      urlSegments.reduce((acc, segment, index, arr) => {
        const path = `${acc}/${segment}`;
        const label = index === arr.length - 1 && breadcrumbData ? breadcrumbData : segment;
        this.breadcrumbs.push({
          label,
          path
        });
        return path;
      }, '');
      // Sanitize the breadcrumb
      this.sanitizeBreadcrumb();
    }
    /**
     * Get the URL from the route and replace the parameters with the actual values
     * @param route ActivatedRouteSnapshot
     * @param path string
     * @returns string
     */
    getUrl(route, path) {
      let url = route.routeConfig?.path;
      if (route.params) {
        Object.keys(route.params).forEach(key => {
          url = this.routeParamsIncludesLocale(route.params[key]) ? url?.replace(`:${key}/`, '') : url?.replace(`${key}`, route.params[key]);
        });
      }
      return path ? `${path}/${url}` : url;
    }
    /**
     * Get the label from the route data
     * @param data Data
     * @returns string
     */
    getLabel(data) {
      return typeof data.breadcrumb === 'function' ? data.breadcrumb(data) : data.breadcrumb;
    }
    /**
     * Sanitize the breadcrumb
     */
    sanitizeBreadcrumb() {
      this.removeOrphanedBreadcrumb();
      this.removeColonsFromBreadcrumb();
      this.removeHyphensFromBreadcrumb();
    }
    /**
     * Remove breadcrumb that doesn't make sense without the parameter that follows it
     */
    removeOrphanedBreadcrumb() {
      const index = this.breadcrumbs.findIndex(item => item.path.includes(':')) - 1;
      if (index > -1) {
        this.breadcrumbs.splice(index, 1);
      }
    }
    /**
     * Remove the colons from the breadcrumb label and path if are present
     */
    removeColonsFromBreadcrumb() {
      this.breadcrumbs = this.breadcrumbs.map(item => {
        return {
          label: item.label.replace(/:/g, ''),
          path: item.path.replace(/:/g, '')
        };
      });
    }
    /**
     * Remove the hyphens from the breadcrumb label and path if are present
     */
    removeHyphensFromBreadcrumb() {
      this.breadcrumbs = this.breadcrumbs.map(item => {
        return {
          label: item.label.replace(/-/g, ' '),
          path: item.path.replace(/-/g, ' ')
        };
      });
    }
    /**
     * Identifies if a section of the route params could be a locale, to remove it from the breadcrumb
     */
    routeParamsIncludesLocale(routeParam) {
      let includesLocale = false;
      // This regex should identify all strings that looks like a locale, with a format similar to 'es-es'
      const localeRegex = new RegExp('^[a-z]{2}-[a-z]{2}');
      // @TODO: Check this to identify better if the breacrumb section belongs to a locale one
      if (routeParam.match(localeRegex)) {
        includesLocale = true;
      }
      return includesLocale;
    }
    static {
      this.ɵfac = function BreadcrumbService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BreadcrumbService)(i0.ɵɵinject(i1$1.Router));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BreadcrumbService,
        factory: BreadcrumbService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BreadcrumbService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ContactService = /*#__PURE__*/(() => {
  class ContactService {
    constructor(samAngularService) {
      this.samAngularService = samAngularService;
      this.samContext = this.samAngularService.getContext();
    }
    getZendeskForm$(getZendeskFormParams) {
      return new GetZendeskContactFormUseCase(this.samContext).execute(getZendeskFormParams);
    }
    sendContactForm$(sendContactFormParams) {
      return new SendContactFormUseCase(this.samContext).execute(sendContactFormParams);
    }
    static {
      this.ɵfac = function ContactService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ContactService)(i0.ɵɵinject(SamAngularService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ContactService,
        factory: ContactService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ContactService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CustomerService = /*#__PURE__*/(() => {
  class CustomerService {
    constructor(samAngularService) {
      this.samAngularService = samAngularService;
      this.samContext = this.samAngularService.getContext();
    }
    /**
     *
     * @deprecated this endpoint need an oauth_public_token. It's retrieved in sam side. It's deprecated until we can do a implementation without tokens or with v5 API
     */
    registerCustomer$(customer, password) {
      return registerUseCase(this.samContext).execute({
        customer: customer,
        password: password
      });
    }
    /**
     *
     * @deprecated this endpoint is using a v4 oauth_user token retrieved from the URL, until the v5 login implementation is completed
     */
    getCustomer$() {
      return getCustomerUseCase(this.samContext).execute({});
    }
    tryGetCustomer$() {
      return tryGetCustomerUseCase(this.samContext).execute({});
    }
    subscribeToNewsletter$(email, subscribe_newsletter, subscribe_promotion, extra) {
      return subscribeToNewsletterUseCase(this.samContext).execute({
        email,
        subscribe_newsletter,
        subscribe_promotion,
        extra
      });
    }
    loginM2M(accessToken) {
      return loginM2MUseCase(this.samContext, accessToken);
    }
    /**
     *
     * @deprecated this endpoint is using an v4 oauth_user token retrieved from the URL . It's deprecated until we can do a implementation with v5 API
     */
    updateCustomer$(customer) {
      return updateCustomerInfoUseCase(this.samContext).execute({
        customer
      });
    }
    /**
     *
     * @deprecated this endpoint is deprecated until we can do a implementation with v5 API
     */
    forgotPassword$(email) {
      return forgotPasswordUseCase(this.samContext).execute({
        email: email,
        lang: 'en',
        redirectURL: '/resetpassword'
      });
    }
    /**
     *
     * @deprecated this endpoint need a privileged access token, it is deprecated until we can do a implementation without tokens or with v5 API
     */
    resetPassword$(customerId, password, token) {
      return resetPasswordUseCase(this.samContext).execute({
        customerId: customerId,
        password: password,
        token: token
      });
    }
    /**
     * @WARNING: this method should be only use from app.component.ts. After loginM2M you should use login.service methods and other services.
     * This is a specific method for the Hybrid Store.
     * We retrieve the specific token key 'pep_storage_oralb-store' from v4 OralB login
     * and pass that information to v5 store, so we can know if the user is logged or not.
     */
    getHybridStoreCredentials() {
      // In SSR mode will return undefined
      const {
        pepStorageKey
      } = this.samContext.apiContext.v4;
      const credentialsString = globalThis.sessionStorage?.getItem(pepStorageKey);
      if (!credentialsString) {
        return;
      }
      return JSON.parse(credentialsString)?.access_token;
    }
    removeCredentialsM2M() {
      removeCredentialsM2MUseCase(this.samContext);
    }
    static {
      this.ɵfac = function CustomerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CustomerService)(i0.ɵɵinject(SamAngularService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CustomerService,
        factory: CustomerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CustomerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const imageLoader = config => {
  const {
    src,
    loaderParams
  } = config;
  const logger = loaderParams?.logger;
  if (!loaderParams) {
    const configuration = JSON.stringify(config);
    throw new Error(`Invalid config for Pandora custom image loader. Missing loaderParams parameter, ${configuration}`);
  }
  if (loaderParams.product) {
    return optimizeProductImage(config, logger);
  } else if (loaderParams.builder) {
    return optimizeBuilderImage(config);
  } else {
    return src;
  }
};
function optimizeProductImage(config, logger) {
  const {
    src,
    width
  } = config;
  if (!src) {
    if (!config.loaderParams?.fallback) {
      logger?.error('Config src is not provided and no fallback for V4 Product Image Loader', config);
      return '';
    }
    return config.loaderParams.fallback;
  }
  // It may reprocess with the fallback as src
  if (src === config.loaderParams?.fallback) {
    return src;
  }
  if (!validateWidth(config, logger)) {
    return src;
  }
  if (!/\.(jpg|jpeg|png|webp|avif|gif)$/.test(src)) {
    return src;
  }
  const srcArr = src.split('/');
  srcArr[2] += `/resize/${width}x-`;
  return srcArr.join('/');
}
function optimizeBuilderImage(config) {
  const {
    src,
    width
  } = config;
  const url = new URL(src);
  url.searchParams.set('format', 'webp');
  if (width) {
    url.searchParams.set('width', width.toString());
  }
  return url.href;
}
function validateWidth(config, logger) {
  const {
    width
  } = config;
  if (!width) {
    logger?.debug('Config width is not provided for V4 Product Image Loader (this may be a component initialization issue)', config);
    return false;
  }
  if (!Number.isInteger(width)) {
    logger?.error('Config width is not integer for V4 Product Image Loader', config);
    return false;
  }
  if (width > 5000 || width < 1) {
    logger?.error('Config width is greater than 5000 or less than 1 for V4 Product Image Loader', config);
    return false;
  }
  return true;
}
let LayoutService = /*#__PURE__*/(() => {
  class LayoutService {
    constructor(router) {
      this.router = router;
      this.pageLayout = new BehaviorSubject(undefined);
    }
    get pageLayout$() {
      return this.pageLayout.asObservable();
    }
    setPageLayout(state) {
      this.pageLayout.next(state);
    }
    resolveLayoutForCurrentRoute() {
      return this.router.events.pipe(filter$1(event => this.filterEvents(event)),
      // event is ActivationEnd ensures that event is of type ActivationEnd
      map$1(event => this.determineLayout(event)),
      // In this switchMap, we check if the page is a POH page or not
      catchError$1(error => {
        console.error('Error determining layout', error);
        return of({
          id: PageLayout.Default,
          data: null
        });
      }));
    }
    filterEvents(event) {
      if (event.type == 14 && event instanceof ActivationEnd) {
        return true;
      }
      if (event.type == 1 && event instanceof NavigationEnd) {
        return true;
      }
      return false;
    }
    determineLayout(event) {
      return this.getLayoutFromEvent(event);
    }
    getLayoutFromEvent(event) {
      if (event instanceof ActivationEnd) {
        this.setActivationLayout(event);
        return undefined;
      } else {
        return this.lastState;
      }
    }
    setActivationLayout(event) {
      let id = PageLayout.Default;
      let data = null;
      if (event.snapshot.data.layout) {
        id = event.snapshot.data.layout;
      } else if (event.snapshot.data.section?.layout) {
        id = event.snapshot.data.section.layout;
      } else if (event.snapshot.data.page?.pageType) {
        id = event.snapshot.data.page.pageType;
        data = event.snapshot.data.page;
      }
      this.lastState = {
        id: id,
        data: data
      };
    }
    static {
      this.ɵfac = function LayoutService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LayoutService)(i0.ɵɵinject(i1$1.Router));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LayoutService,
        factory: LayoutService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LayoutService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * @deprecated we can't use v4 config directly. we have to move it to config-angular package and probably keep it static for now
 */
let LegacyV4ConfigService = /*#__PURE__*/(() => {
  class LegacyV4ConfigService {
    constructor(legacyConfigService, localizationConfigService) {
      this.legacyConfigService = legacyConfigService;
      this.localizationConfigService = localizationConfigService;
      this.config$$ = new ReplaySubject();
      this.config$ = this.config$$.asObservable();
      this.homeSlideshow$ = this.config$.pipe(map(config => config.homeSlideshow));
      this.homeModules$ = this.config$.pipe(map(config => config.homeModules));
      this.hasPromotions$ = this.config$.pipe(map(config => config.hasPromotions));
      this.metacolorReferences$ = this.config$.pipe(map(config => config.metacolorReferences));
      const legacyConfig = this.legacyConfigService.get();
      const localizationConfig = this.localizationConfigService.get();
      this.currentLang = localizationConfig.currentLang;
      this.territory = localizationConfig.territories.find(() => true) || 'REU';
      this.config$$.next(legacyConfig);
      this.currency$ = new BehaviorSubject(localizationConfig.currency).asObservable();
    }
    static {
      this.ɵfac = function LegacyV4ConfigService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LegacyV4ConfigService)(i0.ɵɵinject(i1.LegacyConfigService), i0.ɵɵinject(i1.LocalizationConfigService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LegacyV4ConfigService,
        factory: LegacyV4ConfigService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LegacyV4ConfigService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let GenericLogger = /*#__PURE__*/(() => {
  class GenericLogger extends Logger {
    constructor() {
      const universalLoggerService = inject(UniversalLoggerService);
      super(universalLoggerService.loggerService.getLogger().child({
        module: 'generic'
      }));
    }
    static {
      this.ɵfac = function GenericLogger_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || GenericLogger)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: GenericLogger,
        factory: GenericLogger.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return GenericLogger;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LoginService = /*#__PURE__*/(() => {
  class LoginService {
    constructor(samAngularService) {
      this.samAngularService = samAngularService;
      this.isLogged$ = new BehaviorSubject(false);
      this.samContext = this.samAngularService.getContext();
      isLoggedUseCase(this.samContext).pipe(takeUntilDestroyed()).subscribe(isLogged => this.isLogged$.next(isLogged));
      // We call try get customer so we can get the customer info if the user is logged
      // tryGetCustomerUseCase(this.samContext).execute();
    }
    login$(customer) {
      return loginUseCase(this.samContext).execute({
        grantType: 'password',
        username: customer.email,
        password: customer.password
      });
    }
    logout() {
      logOutUseCase(this.samContext);
    }
    isLogged() {
      return this.isLogged$.asObservable();
    }
    isLoggedSync() {
      return this.isLogged$.getValue();
    }
    static {
      this.ɵfac = function LoginService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LoginService)(i0.ɵɵinject(SamAngularService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LoginService,
        factory: LoginService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LoginService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const MAINTENANCE_MODE_CONFIG = new InjectionToken('maintenanceModeConfig');
const DATE_NOW_FUNCTION = new InjectionToken('dateNowFunction');
/**
 * It manages the maintenance mode status to display or not the maintenance-component.
 * The maintenance mode config comes from the MAINTENANCE_MODE_CONFIG injectionToken.
 * This service is also in charge of adding the maintenance Dependency
 */
let MaintenanceService = /*#__PURE__*/(() => {
  class MaintenanceService {
    constructor(meCachisDependencyService, maintenanceModeConfig, now) {
      this.meCachisDependencyService = meCachisDependencyService;
      this.maintenanceModeConfig = maintenanceModeConfig;
      this.now = now;
    }
    /**
     * It returns the current maintenance mode from maintenance config list, and adds the maintenance dependency.
     */
    getCurrentMaintenanceMode() {
      const currentTimeStamp = this.now();
      const currentMaintenanceMode = this.getCurrentMaintenanceConfig(currentTimeStamp);
      this.addMaintenanceDependency(currentTimeStamp);
      return currentMaintenanceMode.enabled;
    }
    /**
     * It returns the next maintenance config, depending on the current date, from maintenance config list
     */
    getNextMaintenanceConfig(currentTimeStamp) {
      const orderedMaintenanceConfigList = this.getSortedMaintenanceConfigByTimeStamp();
      return orderedMaintenanceConfigList.find(config => config.timeStamp > currentTimeStamp);
    }
    /**
     * It returns the current maintenance state, depending on the currentTimeStamp, from maintenance config list
     */
    getCurrentMaintenanceConfig(currentTimeStamp) {
      const defaultMaintenanceConfig = {
        enabled: false
      };
      const orderedMaintenanceConfigList = this.getSortedMaintenanceConfigByTimeStamp();
      orderedMaintenanceConfigList.reverse();
      return orderedMaintenanceConfigList.find(config => config.timeStamp < currentTimeStamp)?.maintenanceState ?? defaultMaintenanceConfig;
    }
    getSortedMaintenanceConfigByTimeStamp() {
      return this.maintenanceModeConfig.sort((firstConfig, secondConfig) => firstConfig.timeStamp - secondConfig.timeStamp);
    }
    /**
     * It add the Maintenance Dependency if there is a next maintenance config.
     * Otherwise, we don't create the dependency
     */
    addMaintenanceDependency(currentTimeStamp) {
      const nextMaintenanceConfig = this.getNextMaintenanceConfig(currentTimeStamp);
      if (nextMaintenanceConfig) {
        this.meCachisDependencyService.addMaintenanceDependency(nextMaintenanceConfig.timeStamp);
      }
    }
    static {
      this.ɵfac = function MaintenanceService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MaintenanceService)(i0.ɵɵinject(MECACHIS_DEPENDENCY_SERVICE), i0.ɵɵinject(MAINTENANCE_MODE_CONFIG), i0.ɵɵinject(DATE_NOW_FUNCTION));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MaintenanceService,
        factory: MaintenanceService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MaintenanceService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * this mock class is only created for providing it in the components where maintenance service is required but not used (category-page component)
 */
class MaintenanceServiceMock {
  getCurrentMaintenanceMode() {
    return false;
  }
}
const MECACHIS_MEMOIZE = new InjectionToken('MeCachisMemoize');
let MeCachisMemoizeServerSideService = /*#__PURE__*/(() => {
  class MeCachisMemoizeServerSideService {
    #meCachisMemoize;
    constructor(invalidationTimestamp) {
      this.invalidationTimestamp = invalidationTimestamp;
      const meCachisMemoize = inject(MECACHIS_MEMOIZE);
      this.#meCachisMemoize = meCachisMemoize;
    }
    /**
     * Stores a value associated with a specific key.
     * @param key - The key to associate with the value.
     * @param value - The value to be stored.
     */
    set(key, value) {
      this.#meCachisMemoize.set(key, value);
    }
    /**
     * Retrieves a value associated with a specific key.
     * @param key - The key whose associated value is to be returned.
     * @returns A promise that resolves to the value associated with the key, or undefined if the key does not exist.
     */
    async get(key) {
      return this.#meCachisMemoize.get(key, this.invalidationTimestamp);
    }
    static {
      this.ɵfac = function MeCachisMemoizeServerSideService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || MeCachisMemoizeServerSideService)(i0.ɵɵinject('INVALIDATION_TIMESTAMP', 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: MeCachisMemoizeServerSideService,
        factory: MeCachisMemoizeServerSideService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return MeCachisMemoizeServerSideService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
class MetaproductSelectorCheapestService extends MetaproductSelectorBase {
  productSpecificSelector(product) {
    const metachildrens = this.getChildMetaProduct(product);
    return this.getProductCheapest(product, metachildrens);
  }
  getProductCheapest(product, childrens) {
    const cheapestChild = this.getCheapestChild(product, childrens);
    if (!cheapestChild) {
      throw new Error(`Impossible to navigate to the cheapest available child: valid children within the meta-product(${product.id}) not found`);
    }
    return cheapestChild.productID;
  }
  getCheapestChild(product, childrens) {
    let cheapestChild = null;
    for (const child of childrens) {
      if (this.isValidProduct(child, product) && (!cheapestChild || parseFloat(child.price) < parseFloat(cheapestChild.price))) {
        cheapestChild = child;
      }
    }
    return cheapestChild;
  }
}
class MetaproductSelectorFirstAvailableService extends MetaproductSelectorBase {
  productSpecificSelector(product) {
    return this.getProductIdOfFirstAvailableChild(product, this.getChildMetaProduct(product));
  }
  /**
   * Get the productId of the first available child of a meta-product
   * @param product Product
   * @returns number
   */
  getProductIdOfFirstAvailableChild(product, childrens) {
    const children = childrens.find(child => this.isValidProduct(child, product));
    if (!children) {
      throw new Error(`Impossible to navigate to the first available child: valid children within the meta-product(${product.id}) not found`);
    }
    return children.productID;
  }
}
class LocalizeHelper {
  static getLocaleData(data, locale) {
    if (typeof data == 'object' && data['@type'] && data['@type'] == '@builder.io/core:LocalizedValue') {
      data = data['Default'] ?? '';
      if (data[locale]) {
        data = data[locale];
      }
    }
    return data;
  }
}

/**
 * Service to manage page navigation outside of Angular routing context.
 */
let ServerNavigateService = /*#__PURE__*/(() => {
  class ServerNavigateService {
    constructor() {
      this.logger = inject(GenericLogger);
      this.location = inject(Location);
    }
    /**
     * Navigates to the given route by reloading the page.
     * The URL is constructed from the route parameters and the base href configured in the application.
     * The promise will not resolve under normal conditions since the page reload interrupts script execution.
     * If the navigation does not occur within the specified timeout, the promise is rejected.
     *
     * @param route The route to navigate to.
     * @returns A promise that remains unresolved if the navigation occurs, or is rejected if the timeout (5000ms) is exceeded.
     */
    navigateToRoute(route) {
      const routeParams = Object.values(route.params);
      const url = this.getExternalUrl(`/${routeParams.join('/')}`);
      return new Promise((resolve, reject) => {
        this.logger.info(`Navigating to page ${url} using server navigation`);
        window.location.assign(url);
        // Reject the promise if the navigation does not occur within the timeout
        const navigationTimeout = 5000;
        setTimeout(() => {
          reject(new Error(`Server navigation timeout of ${navigationTimeout} ms exceeded for ${url}`));
        }, navigationTimeout);
      });
    }
    /**
     * Constructs an external URL adding the base href to the URL.
     * @param url URL to prepare for external use.
     * @returns The external URL.
     */
    getExternalUrl(url) {
      return this.location.prepareExternalUrl(url);
    }
    static {
      this.ɵfac = function ServerNavigateService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ServerNavigateService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ServerNavigateService,
        factory: ServerNavigateService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ServerNavigateService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This function checks that the received object is an array of results and that each element has a data object and a
 * page property inside.
 * This helps us to identify if the request to Builder API was done with the specific 'page' field parameter, and so,
 * the Builder API response will include the page object.
 */
function isBuilderPageResults(obj) {
  return obj && Array.isArray(obj.results) && obj.results.every(element => element.data && element.data.page !== undefined);
}
let BuilderService = /*#__PURE__*/(() => {
  class BuilderService {
    #builderIOApiKey;
    #isBuilderEditor = inject(IS_BUILDER_EDITOR, {
      optional: true
    });
    #httpClient = inject(HttpClient);
    #builderConfigService = inject(BuilderConfigService);
    #logger = inject(GenericLogger);
    #mecachisDependencyService = inject(MECACHIS_DEPENDENCY_SERVICE);
    constructor() {
      this.#builderIOApiKey = this.#builderConfigService.get().builderIOApiKey;
    }
    /**
     * It gets the content from builder API by the modelName. It's used from different elements in the application.
     * @param modelName The modelName to get the content from Builder.io
     * @returns Observable of BuilderContent
     */
    getContentByModelName(modelName) {
      return this.getContentsByModelName(modelName).pipe(map$1(content => {
        if (!content[0]) {
          this.#mecachisDependencyService.addErrorDependency(`BuilderContentByModel: No content found for model ${modelName}`);
          throw new BuilderContentError(`BuilderContentByModel: No content found for model ${modelName}`);
        }
        if (content.length > 1) {
          this.#logger.warn({
            msg: `getContentByModelName. Unexpected more than one result found for model ${modelName}`,
            modelName
          });
        }
        return content[0];
      }));
    }
    /**
     * It gets the array of contents from builder API by the modelName. It's used from different elements in the application.
     * @param modelName The modelName to get the content from Builder.io
     * @param params Some elements send their own params to the API to filter the content.
     * @returns Observable of BuilderContent[]
     */
    getContentsByModelName(modelName, params) {
      const url = `https://cdn.builder.io/api/v3/content/${modelName}?apiKey=${this.#builderIOApiKey}&limit=100`;
      return this.#httpClient.get(url, {
        params
      }).pipe(map$1(builderContent => {
        if (!this.#isBuilderResults(builderContent)) {
          this.#mecachisDependencyService.addErrorDependency(`BuilderContentsByModel: Invalid content for model ${modelName}`);
          throw new BuilderContentError(`BuilderContentsByModel: Invalid content for model ${modelName}`);
        }
        return [...builderContent.results];
      }), tap$1(builderContent => {
        this.#logger.debug({
          msg: `getContentsByModelName addBuilderDependency ${modelName}`,
          builderContent
        });
        // This check is correct, because the content is already filtered by the find statement
        const contentToRevalidate = builderContent.map(({
          id,
          lastUpdated
        }) => ({
          id: id,
          lastUpdated: lastUpdated
        }));
        this.#mecachisDependencyService.addRevalidableBuilderDependency(modelName, {
          contentToRevalidate,
          url,
          apiKey: this.#builderIOApiKey,
          queryType: 'content',
          timeToRevalidate: 60 * 1
        });
      }));
    }
    /**
     * It checks if the content received from the API has the expected structure.
     * @param builderContent The content received from the Builder API.
     * @returns type predicate
     */
    #isBuilderResults(builderContent) {
      const builderContents = builderContent?.results;
      if (!Array.isArray(builderContents)) {
        return false;
      }
      return builderContents.every(content => {
        if (!content) {
          return false;
        }
        return content.id && content.lastUpdated;
      });
    }
    /**
     * It gets the page content from Builder.io by the builderParams received.
     * The builderParams have the urlPath and queryParams to get the content.
     * This function is used by the page handler and the prefetch service to get the page content.
     * If the content is empty we return undefined, so the page handler can manages it as a Contentful page or a 404 not found.
     * @param builderParams Builder params created from the urlPath and queryParams
     * @returns Observable of BuilderPageData | undefined
     */
    getPageByParams(builderParams) {
      const url = `https://cdn.builder.io/api/v3/content/page`;
      return this.#httpClient.get(url, {
        params: builderParams,
        transferCache: false
      }).pipe(map$1(content => {
        if (!content.results.length) {
          return;
        }
        if (content.results.length > 1) {
          this.#logger.warn({
            msg: `getPageByParamsHttp. Unexpected more than one result found for page ${builderParams['userAttributes.urlPath']}`,
            builderParams
          });
        }
        return content.results[0].data;
      }), tap$1(builderContent => {
        if (!builderContent) {
          this.#logger.info({
            msg: `getPageByParams. No Builder content found for page ${builderParams['userAttributes.urlPath']}`,
            builderParams
          });
          return;
        }
        let pathToData;
        let auxBuilderContent;
        if (builderContent.page.value) {
          pathToData = 'data.page.value';
          auxBuilderContent = builderContent.page.value;
        } else {
          auxBuilderContent = builderContent;
        }
        if (!auxBuilderContent.id || !auxBuilderContent.lastUpdated) {
          this.#logger.warn({
            msg: `getPageByParams builderContent: No lastUpdated or id found for page ${builderParams['userAttributes.urlPath']}`,
            builderContent: auxBuilderContent,
            options: builderParams
          });
          return;
        }
        this.#logger.debug({
          msg: `getPageByParams addBuilderDependency for page ${builderParams['userAttributes.urlPath']}`,
          builderContent: auxBuilderContent
        });
        // This check is correct, because the content is already filtered by the find statement
        const contentToRevalidate = [{
          id: auxBuilderContent.id,
          lastUpdated: auxBuilderContent.lastUpdated
        }];
        this.#mecachisDependencyService.addRevalidableBuilderDependency('page', {
          contentToRevalidate,
          url,
          apiKey: this.#builderIOApiKey,
          queryType: 'content',
          querySearchParams: new URLSearchParams(builderParams),
          pathToData,
          timeToRevalidate: 60 * 1
        });
      }));
    }
    /**
     * Get the Builder most popular pages set as prefetch in Builder.io
     * These prefetch operations manage its own stateTransfer. Angular transferCache is disabled for this request.
     * @returns Promise of BuilderPageResultData[]
     */
    async prefetchMostPopularPages() {
      const params = {
        omit: 'meta.componentsUsed',
        'query.data.prefetch.$eq': true,
        enrich: true,
        fields: 'data.page,data.serverNavigation',
        apiKey: this.#builderIOApiKey
      };
      return firstValueFrom(this.#httpClient.get(`https://cdn.builder.io/api/v3/content/page`, {
        params,
        transferCache: false
      })).then(builderContent => {
        if (isBuilderPageResults(builderContent)) {
          return builderContent.results;
        }
        throw new Error('BuilderContentService: PrefetchMostPopularPages response is not of the expected type BuilderPageResultData');
      });
    }
    /**
     * It creates the Builder params needed for the page request.
     * The params are used to get the page content from Builder.io.
     * @param path url requested
     * @param queryParams params received from the url
     * @returns the builder params object
     */
    getPageRequestParams(path, queryParams) {
      const args = {
        omit: 'meta.componentsUsed',
        enrich: true,
        fields: 'data.page,data.serverNavigation',
        'userAttributes.urlPath': path,
        apiKey: this.#builderIOApiKey
      };
      if (this.#isBuilderEditor) {
        // Include unpublished pages if current state is builder editor
        args['includeUnpublished'] = true;
      }
      if (this.#isBuilderEditor && queryParams?.builderid) {
        // Get page by id to show scheduled pages in builder editor
        delete args['userAttributes.urlPath'];
        args['query.id.$eq'] = queryParams.builderid;
      }
      return args;
    }
    static {
      this.ɵfac = function BuilderService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BuilderService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BuilderService,
        factory: BuilderService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BuilderService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PageBuilderResolver = /*#__PURE__*/(() => {
  class PageBuilderResolver {
    #builderService = inject(BuilderService);
    #locale = inject(LOCALE_ID);
    #router = inject(Router);
    #serverNavigateService = inject(ServerNavigateService);
    #builderPrefetchService = inject(BuilderPrefetchService);
    #logger = inject(GenericLogger);
    constructor() {
      this.#builderPrefetchService.prefetchMostPopular().catch(error => this.#logger.error('PageBuilderResolver: Unexpected error prefetching Builder most popular pages', error));
    }
    async resolve(route) {
      const routeParams = Object.values(route.params);
      const builderData = await this.#getPageData(`/${routeParams.join('/')}`, route.queryParams);
      if (!builderData) {
        return null;
      }
      // The page is loaded with a server navigation when the user accesses it to avoid slow loading times.
      // This reload causes the requested page data to be performed on the server side.
      if (this.#router.navigated && builderData.extraData?.serverNavigation) {
        await this.#serverNavigateService.navigateToRoute(route);
      }
      return builderData;
    }
    #formatPageData(builderData) {
      const data = builderData.page.value.data;
      const metadata = this.#getPageMetadata(data.seoTags);
      const pageData = {
        pageProvider: 'builder.io',
        name: LocalizeHelper.getLocaleData(data.title, this.#locale),
        type: data.layout,
        pageType: this.#getLayout(data.layout),
        extraData: {
          title: LocalizeHelper.getLocaleData(data.title, this.#locale),
          subCategories: [],
          id: builderData.page.value.id,
          customBreadcrumbs: this.#getBreadCrumbs(data, []),
          path: data.url,
          metadata: metadata,
          categoryBanner: data.categorybanner ?? null,
          components: data.components ? PageComponents.fromBuilder(data.components) : undefined,
          serverNavigation: builderData.serverNavigation ?? null
        }
      };
      return pageData;
    }
    #getPageMetadata(seoTags) {
      const temporalData = seoTags ? LocalizeHelper.getLocaleData(seoTags, this.#locale) : null;
      if (temporalData) {
        return temporalData.value?.data;
      }
      return temporalData;
    }
    async #getPageData(path, queryParams) {
      const builderParams = this.#builderService.getPageRequestParams(path, queryParams);
      const prefetchedContent = await this.#builderPrefetchService.getPrefetchedPage(builderParams);
      if (prefetchedContent) {
        return this.#formatPageData(prefetchedContent);
      }
      try {
        const info = await firstValueFrom(this.#builderService.getPageByParams(builderParams));
        if (!info) {
          this.#logger.debug({
            msg: 'PageBuilderHandler: No page found in Builder with the given path',
            path
          });
          return null;
        }
        return this.#formatPageData(info);
      } catch (error) {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 404) {
            this.#logger.error({
              msg: 'PageBuilderHandler: Unexpected 404 error requesting Builder page for this path. Page Model request failed',
              path
            });
          }
        } else {
          this.#logger.error({
            msg: 'PageBuilderHandler: Unexpected error handling Builder Page',
            error
          });
        }
        return null;
      }
    }
    #getLayout(layout) {
      return PageLayout.Default;
    }
    #getBreadCrumbs(page, breadcrumbs) {
      if (page && page.breadcrumbs && page.breadcrumbs.parent) {
        this.#getBreadCrumbs(page.breadcrumbs.parent.value.data, breadcrumbs);
      }
      const label = page.breadcrumbs && page.breadcrumbs.title ? LocalizeHelper.getLocaleData(page.breadcrumbs.title, this.#locale) : LocalizeHelper.getLocaleData(page.title, this.#locale);
      const path = page.url;
      breadcrumbs.push({
        label: label,
        path: path
      });
      return breadcrumbs;
    }
    static {
      this.ɵfac = function PageBuilderResolver_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PageBuilderResolver)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PageBuilderResolver,
        factory: PageBuilderResolver.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PageBuilderResolver;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let PageNotFoundResolver = /*#__PURE__*/(() => {
  class PageNotFoundResolver {
    async resolve() {
      return null;
    }
    static {
      this.ɵfac = function PageNotFoundResolver_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PageNotFoundResolver)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PageNotFoundResolver,
        factory: PageNotFoundResolver.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PageNotFoundResolver;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const pageResolver = async route => {
  const pageResolvers = inject(PAGE_RESOLVERS);
  for (const resolver of pageResolvers) {
    const page = await resolver.resolve(route);
    if (!page) {
      continue;
    }
    return page;
  }
  return null;
};

/**
 * This provides a list of the page resolvers used in the store.
 * We use this factory function with the PAGE_RESOLVERS injection token key.
 * In this way the services will be available to be used for resolving pages.
 */
function providePageResolvers() {
  return {
    provide: PAGE_RESOLVERS,
    useFactory: (pageBuilderResolver, pageNotFoundResolver) => {
      return [pageBuilderResolver, pageNotFoundResolver];
    },
    deps: [PageBuilderResolver, PageNotFoundResolver]
  };
}

/**
 * Service for handling express server response.
 * This is a universal service that can be used in both server and browser environments.
 */
let ServerResponseService = /*#__PURE__*/(() => {
  class ServerResponseService {
    constructor() {
      this.dependencyService = inject(MECACHIS_DEPENDENCY_SERVICE);
      this.serverProvidersRequired = inject(SERVER_PROVIDERS_REQUIRED, {
        optional: true
      });
      this.serverResponse = inject(EXPRESS_RESPONSE, {
        optional: true
      });
      this.location = inject(Location);
      this.platformId = inject(PLATFORM_ID);
      // When running the dev-server we don't have the server logger available so we fake it with the browser logger
      // This could be done more cleanly but Angular may remove this limitation
      // https://github.com/angular/angular-cli/issues/26323
      if (!this.serverResponse && this.serverProvidersRequired) {
        throw new Error('EXPRESS_RESPONSE inject token not available and required');
      }
    }
    /**
     * Sets the server response status code to 404 and adds a not found dependency.
     */
    setNotFound() {
      if (!isPlatformServer(this.platformId)) {
        return;
      }
      if (this.serverResponse) {
        this.serverResponse.statusCode = 404;
      }
      this.dependencyService.addNotFoundDependency(this.location.path());
    }
    static {
      this.ɵfac = function ServerResponseService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ServerResponseService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ServerResponseService,
        factory: ServerResponseService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ServerResponseService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let StoreService = /*#__PURE__*/(() => {
  class StoreService {
    constructor(storeConfigService) {
      this.storeConfigService = storeConfigService;
      this.config$$ = new ReplaySubject();
      this.config$ = this.config$$.asObservable();
      this.hasNewsletter$ = this.config$.pipe(map(config => config.hasNewsletter));
      const storeConfig = this.storeConfigService.get();
      this.config$$.next(storeConfig);
    }
    static {
      this.ɵfac = function StoreService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StoreService)(i0.ɵɵinject(i1.StoreConfigService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: StoreService,
        factory: StoreService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return StoreService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let UniversalLocationService = /*#__PURE__*/(() => {
  class UniversalLocationService {
    #request;
    #platformId;
    constructor() {
      // Express request is not available in the server with ng serve, providing a workaround for dev mode
      // TODO:: review this when Angular provides a better solution https://github.com/angular/angular-cli/issues/26323
      this.#request = inject(EXPRESS_REQUEST, {
        optional: true
      }) ?? undefined;
      const serverProvidersRequired = inject(SERVER_PROVIDERS_REQUIRED, {
        optional: true
      });
      this.#platformId = inject(PLATFORM_ID);
      if (isPlatformServer(this.#platformId) && serverProvidersRequired) {
        throw new Error('EXPRESS_REQUEST inject token not available and required');
      }
    }
    // This uses location, we should use Angular router instead
    getHostname() {
      if (isPlatformServer(this.#platformId)) {
        return this.#request?.hostname ?? 'ssr-fallback-hostname';
      } else {
        return location.host;
      }
    }
    /**
     * Returns the current pathname also in the server side
     * @deprecated: This can be obtained from Angular Router universally too, it will be removed in the future
     */
    getCurrentUrl() {
      if (isPlatformServer(this.#platformId)) {
        return this.#request?.originalUrl ?? 'ssr-fallback-original-url';
      } else {
        return location.pathname;
      }
    }
    getProtocol() {
      if (isPlatformServer(this.#platformId)) {
        return this.#request?.protocol ?? 'http';
      } else {
        return location.protocol.split(':')[0];
      }
    }
    static {
      this.ɵfac = function UniversalLocationService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || UniversalLocationService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: UniversalLocationService,
        factory: UniversalLocationService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return UniversalLocationService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let WishlistService = /*#__PURE__*/(() => {
  class WishlistService {
    constructor(router, samAngularService, samConfigService, localizationConfigService, storeConfigService, loginService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    platformId) {
      this.router = router;
      this.samAngularService = samAngularService;
      this.samConfigService = samConfigService;
      this.localizationConfigService = localizationConfigService;
      this.storeConfigService = storeConfigService;
      this.loginService = loginService;
      this.platformId = platformId;
      this.destroyedRef = inject(DestroyRef);
      this.samContext = this.samAngularService.getContext();
      this.samConfig = this.samConfigService.get();
      this.localizationConfig = this.localizationConfigService.get();
      this.APIVersion = this.samConfig.apiContext.v4.apiVersion;
      this.projectID = this.samConfig.apiContext.v4.projectId;
      this.country = this.localizationConfig.currentLang;
      this.loginURL = this.storeConfigService.getCustomRoutes()?.login ?? 'login';
      this.isLogged$ = this.loginService.isLogged();
    }
    checkLoggedUser() {
      this.isLogged$.pipe(take$1(1)).subscribe(isLogged => {
        if (!isLogged) {
          window.location.href = this.loginURL;
        }
      });
    }
    getWishlist$() {
      return this.loginService.isLogged().pipe(switchMap$1(isLogged => {
        if (isLogged) {
          return new GetWishlistUseCase(this.samContext).execute({
            APIVersion: this.APIVersion,
            country: this.country,
            projectID: this.projectID
          });
        }
        return of(undefined);
      }));
    }
    addIntoWishlist$(wishlist, product) {
      const wishlistProducts = JSON.parse(JSON.stringify(wishlist.products));
      wishlistProducts.push({
        productID: product.id,
        quantity: '1',
        price: String(product.price.currencies[0].prices[0].price),
        currency: product.price.currencies[0].currency
      });
      return new UpdateWishlistUseCase(this.samContext).execute({
        APIVersion: this.APIVersion,
        cartID: wishlist.id,
        params: {
          projectID: this.projectID,
          country: this.country,
          quantity: Number(wishlist.quantity) + 1,
          products: wishlistProducts
        }
      });
    }
    removeFromWishlist$(wishlist, product) {
      const wishlistProducts = wishlist.products.filter(wishlistProduct => {
        return wishlistProduct.productID !== product.id;
      });
      return new UpdateWishlistUseCase(this.samContext).execute({
        APIVersion: this.APIVersion,
        cartID: wishlist.id,
        params: {
          projectID: this.projectID,
          country: this.country,
          quantity: Number(wishlist.quantity) - 1,
          products: wishlistProducts
        }
      });
    }
    static {
      this.ɵfac = function WishlistService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || WishlistService)(i0.ɵɵinject(i1$1.Router), i0.ɵɵinject(SamAngularService), i0.ɵɵinject(i1.SamConfigService), i0.ɵɵinject(i1.LocalizationConfigService), i0.ɵɵinject(i1.StoreConfigService), i0.ɵɵinject(LoginService), i0.ɵɵinject(PLATFORM_ID));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: WishlistService,
        factory: WishlistService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return WishlistService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * PrefetchService is used to prefetch page requests in the application so that the data is already available in cache when the user accesses the page.
 * It is used to improve the performance of the application.
 * The prefetch services are defined in the array of Providers with the injection token PREFETCH_SERVICES as key of provide.
 */
let PrefetchService = /*#__PURE__*/(() => {
  class PrefetchService {
    #prefetchServices = inject(PREFETCH_SERVICES);
    async prefetchPage(route, params) {
      for (const service of this.#prefetchServices) {
        if (service.isAlreadyPrefetched(route, params)) {
          return;
        }
        const result = await service.prefetchPage(route, params);
        if (!result) {
          continue;
        }
        return result;
      }
    }
    static {
      this.ɵfac = function PrefetchService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || PrefetchService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: PrefetchService,
        factory: PrefetchService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return PrefetchService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Specialized PrefetchService for Builder pages.
 * This service is responsible for prefetching and caching Builder pages to improve performance and reduce load times.
 * It uses the BuilderContentService to fetch page data and stores the prefetched pages in a cache.
 * The service also handles serialization and deserialization of the prefetched pages for server-side rendering.
 * @implements {PrefetchHandler<BuilderPageData>}
 */
let BuilderPrefetchService = /*#__PURE__*/(() => {
  class BuilderPrefetchService {
    #builderContentService = inject(BuilderService);
    #logger = inject(GenericLogger);
    #transferState = inject(TransferState);
    #prefetchedPages = {};
    #serializedPages = {};
    #platformID = inject(PLATFORM_ID);
    #BUILDER_PREFETCH = makeStateKey('builderPrefetch');
    constructor() {
      if (isPlatformBrowser(this.#platformID)) {
        const data = this.#transferState.get(this.#BUILDER_PREFETCH, {});
        this.#prefetchedPages = Object.fromEntries(Object.entries(data).map(([key, value]) => [key, Promise.resolve(value)]));
      }
      // Save the pages in the transferState. This action is made at the end of Angular process.
      this.#transferState.onSerialize(this.#BUILDER_PREFETCH, () => this.#serializedPages);
    }
    /**
     * Prefetches a Builder page based on the given route and parameters. It's responsible for fetching the page data and storing it in the cache.
     * @param route - The route of the page needed to create the Builder params for the request.
     * @param params - The parameters for the page request needed to create the Builder params for the request.
     * @returns A promise that resolves to the prefetched BuilderPageData or undefined if an error occurs.
     */
    async prefetchPage(route, params) {
      const builderParams = this.#builderContentService.getPageRequestParams(route, params);
      try {
        const pageResultData = firstValueFrom(this.#builderContentService.getPageByParams(builderParams));
        this.setPageInCache(builderParams, pageResultData);
        return pageResultData;
      } catch (error) {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 404) {
            this.#logger.error({
              msg: 'BuilderPrefetchService: Unexpected 404 error requesting Builder page for this path. Page Model request failed',
              route
            });
          }
        } else {
          this.#logger.error({
            msg: 'BuilderPrefetchService: Unexpected error handling Builder Page',
            error
          });
        }
        return;
      }
    }
    /**
     * Caches the Builder page data for the given parameters. We use the builderParams to generate a cache key for the page.
     * This cache set is used to store the prefetched pages, even if they are empty.
     * @param builderParams - The parameters for the page request.
     * @param page - A promise that resolves to the BuilderPageResultData array.
     */
    setPageInCache(builderParams, page) {
      const cacheKey = this.getCacheKey(builderParams);
      this.#prefetchedPages[cacheKey] = page.then(data => data);
      page.then(data => this.#serializedPages[cacheKey] = data);
    }
    /**
     * Generates a cache key based on the given Builder params.
     * @param params - The parameters generated by the BuilderContentService for the page request.
     * @returns A string representing the cache key.
     */
    getCacheKey(params) {
      return JSON.stringify(params, Object.keys(params).sort());
    }
    /**
     * Retrieves the prefetched Builder page data for the given parameters.
     * @param params - The parameters generated by the BuilderContentService for the page request.
     * @returns A promise that resolves to the prefetched BuilderPageData or undefined if not found.
     */
    async getPrefetchedPage(params) {
      const cacheKey = this.getCacheKey(params);
      this.#logger.debug({
        msg: 'BuilderPrefetchService: prefetched data is being used',
        builderCacheKey: cacheKey
      });
      return this.#prefetchedPages[cacheKey];
    }
    /**
     * Checks if a page is already prefetched based on the given route and parameters.
     * As the Builder cache stores all the prefetched pages, we can check if any page is already prefetched, from any kind of request.
     * @param route - The route of the page.
     * @param params - The parameters for the page request.
     * @returns A boolean indicating whether the page is already prefetched.
     */
    isAlreadyPrefetched(route, params) {
      const builderParams = this.#builderContentService.getPageRequestParams(route, params);
      const cacheKey = this.getCacheKey(builderParams);
      return cacheKey in this.#prefetchedPages;
    }
    /**
     * Prefetches the most popular pages using the content API.
     * This method is only executed on the server side.
     */
    async prefetchMostPopular() {
      if (isPlatformBrowser(this.#platformID)) {
        return;
      }
      const mostPopularPages = this.#builderContentService.prefetchMostPopularPages();
      await this.#setMostPopularPagesInCache(mostPopularPages);
    }
    /**
     * Sets the Builder pages results in the object of promises.
     * @param builderResults - A promise that resolves to the BuilderPageResultData array.
     */
    async #setMostPopularPagesInCache(builderResults) {
      const pageResultData = await builderResults;
      pageResultData.forEach(item => {
        const url = item.data.page.value.data?.url;
        const builderParams = this.#builderContentService.getPageRequestParams(url);
        const cacheKey = this.getCacheKey(builderParams);
        this.#prefetchedPages[cacheKey] = Promise.resolve(item.data);
        this.#serializedPages[cacheKey] = item.data;
      });
    }
    static {
      this.ɵfac = function BuilderPrefetchService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BuilderPrefetchService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BuilderPrefetchService,
        factory: BuilderPrefetchService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BuilderPrefetchService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This provides a list of the prefetch services used in the store.
 * We use this factory function with the PREFETCH_SERVICES injection token key.
 * In this way the services will be available to be used for prefetching data.
 * For the Merchandise Store we provide the BuilderPrefetchService only.
 */
function providePrefetch() {
  return {
    provide: PREFETCH_SERVICES,
    useFactory: builderPrefetchService => {
      return [builderPrefetchService];
    },
    deps: [BuilderPrefetchService]
  };
}
let AddToCartService = /*#__PURE__*/(() => {
  class AddToCartService {
    constructor(cartService, productService, storeConfigService, destroyRef) {
      this.cartService = cartService;
      this.productService = productService;
      this.storeConfigService = storeConfigService;
      this.destroyRef = destroyRef;
      this.storeConfig = this.storeConfigService.get();
      this.MAX_ITEM_QUANTITY = this.storeConfig.maxItemQuantity;
    }
    getProduct$(productID) {
      return this.productService.getProduct$(parseInt(productID));
    }
    getProductDescription(product) {
      return product.descriptions[0].long;
    }
    maxOrderValueReached(productsAdded) {
      return this.cartService.productsTillCartFull().pipe(map$1(num => productsAdded - num > 0));
    }
    getProductQuantity(id) {
      return this.cartService.getCartProductQuantity$(id);
    }
    onResetButton(status) {
      return status;
    }
    /**
     * Adds a product to the cart and returns an observable with the updated cart and an error code.
     * @param cartProduct The product to be added to the cart.
     * @returns An observable that emits an object containing the updated cart and an error code, 1 is everything is okay -2 if mas order value is reached
     * and -1 if max product values is reached. We use error code as number as we have to emit the quantity of product to the father.
     */
    onAddToCart(cartProduct) {
      // TODO: This is not the best implementation of this logic, to be refactor when cart service is refactored
      return this.maxOrderValueReached(cartProduct.quantity).pipe(takeUntilDestroyed(this.destroyRef), take$1(1), switchMap$1(maxOrderValueReached => {
        // We need to check the previous quantity to see if we reach the limit
        return this.getProductQuantity(cartProduct.productId).pipe(take$1(1), takeUntilDestroyed(this.destroyRef), switchMap$1(previousQuantity => {
          if (maxOrderValueReached && !previousQuantity) {
            cartProduct.quantity = 0;
            return this.cartService.cart$.pipe(take$1(1), map$1(cart => {
              return {
                cart,
                errorCode: -2
              };
            }));
          }
          if (maxOrderValueReached && previousQuantity) {
            cartProduct.quantity = previousQuantity;
            return this.cartService.cart$.pipe(take$1(1), map$1(cart => {
              return {
                cart,
                errorCode: -2
              };
            }));
          }
          if (previousQuantity && cartProduct.quantity + previousQuantity > this.MAX_ITEM_QUANTITY) {
            cartProduct.quantity = this.MAX_ITEM_QUANTITY;
            return this.cartService.cart$.pipe(take$1(1), map$1(cart => {
              return {
                cart,
                errorCode: -1
              };
            }));
          }
          return this.cartService.addToCart$(cartProduct).pipe(map$1(cart => {
            return {
              cart,
              errorCode: 1
            };
          }));
        }));
      }));
    }
    static {
      this.ɵfac = function AddToCartService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AddToCartService)(i0.ɵɵinject(CartService), i0.ɵɵinject(ProductService), i0.ɵɵinject(i1.StoreConfigService), i0.ɵɵinject(i0.DestroyRef));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: AddToCartService,
        factory: AddToCartService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return AddToCartService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let CrossSellProductsService = /*#__PURE__*/(() => {
  class CrossSellProductsService {
    constructor(productService) {
      this.productService = productService;
      this.crossSellProducts = [];
    }
    getCrossSellProducts(product) {
      return !product ? of([]) : this.productService.getRelatedProducts$(product);
    }
    static {
      this.ɵfac = function CrossSellProductsService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CrossSellProductsService)(i0.ɵɵinject(ProductService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: CrossSellProductsService,
        factory: CrossSellProductsService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return CrossSellProductsService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let LanguageSelectorService = /*#__PURE__*/(() => {
  class LanguageSelectorService {
    setSiteLanguage(_doc) {
      return _doc.location.pathname.split('/')[1];
    }
    setLocation(languageList, _doc) {
      return `/${languageList[0].code}` + _doc.location.pathname;
    }
    /**
     * This function does not update the url of the website, it only does a replace of the href
     * @deprecated
     */
    updateLocation(siteLanguage, _doc, selectedLang) {
      return _doc.location.href.replace(`/${siteLanguage}/`, `/${selectedLang}/`);
    }
    static {
      this.ɵfac = function LanguageSelectorService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LanguageSelectorService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LanguageSelectorService,
        factory: LanguageSelectorService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return LanguageSelectorService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductAddedToCartService = /*#__PURE__*/(() => {
  class ProductAddedToCartService {
    getProductTitle(product) {
      if (!product) {
        return;
      }
      return product.descriptions[0].title;
    }
    static {
      this.ɵfac = function ProductAddedToCartService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductAddedToCartService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductAddedToCartService,
        factory: ProductAddedToCartService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductAddedToCartService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductGalleryService = /*#__PURE__*/(() => {
  class ProductGalleryService {
    constructor(productService) {
      this.productService = productService;
    }
    getGallery$(productId) {
      if (!productId) {
        return;
      }
      return this.productService.getProduct$(parseInt(productId)).pipe(map(data => data.gallery));
    }
    static {
      this.ɵfac = function ProductGalleryService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductGalleryService)(i0.ɵɵinject(ProductService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductGalleryService,
        factory: ProductGalleryService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductGalleryService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductPriceService = /*#__PURE__*/(() => {
  class ProductPriceService {
    constructor(productService) {
      this.productService = productService;
    }
    getPrice$(productId) {
      if (!productId) {
        console.log('No product id');
        return;
      }
      return this.productService.getProduct$(parseInt(productId)).pipe(map(product => {
        return product.price;
      }));
    }
    static {
      this.ɵfac = function ProductPriceService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductPriceService)(i0.ɵɵinject(ProductService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductPriceService,
        factory: ProductPriceService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductPriceService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductShortDescriptionService = /*#__PURE__*/(() => {
  class ProductShortDescriptionService {
    constructor(productService) {
      this.productService = productService;
    }
    getShortDesc$(productId, locale) {
      if (productId) {
        return of('');
      }
      return this.productService.getProduct$(parseInt(productId)).pipe(map(product => {
        const productDescription = product.descriptions.find(description => description.lang === locale.toUpperCase()) || product.descriptions[0];
        return productDescription.short;
      }));
    }
    static {
      this.ɵfac = function ProductShortDescriptionService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductShortDescriptionService)(i0.ɵɵinject(ProductService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductShortDescriptionService,
        factory: ProductShortDescriptionService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductShortDescriptionService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let ProductTitleService = /*#__PURE__*/(() => {
  class ProductTitleService {
    constructor(productService) {
      this.productService = productService;
    }
    getTitle$(productId, locale) {
      if (!productId) {
        return of('');
      }
      return this.productService.getProduct$(parseInt(productId)).pipe(map(product => {
        const productDescription = product.descriptions.find(description => description.lang === locale.toUpperCase()) || product.descriptions[0];
        return productDescription.title;
      }));
    }
    static {
      this.ɵfac = function ProductTitleService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || ProductTitleService)(i0.ɵɵinject(ProductService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: ProductTitleService,
        factory: ProductTitleService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return ProductTitleService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SearchBarService = /*#__PURE__*/(() => {
  class SearchBarService {
    constructor(router, featureFlagsConfigService, storeSearchService, destroyRef) {
      this.router = router;
      this.featureFlagsConfigService = featureFlagsConfigService;
      this.storeSearchService = storeSearchService;
      this.destroyRef = destroyRef;
      this.searchBarEvents$$ = new ReplaySubject();
      this.minWordsCount = 3;
      this.searchDebounceTime = 150;
      this.filter = {
        name: 'search',
        values: [],
        groupBy: 'filters'
      };
      this.searchBarEvents$ = this.searchBarEvents$$.pipe(debounceTime(this.searchDebounceTime), filter$1(filters => filters != '' && filters.search && filters.search.length >= this.minWordsCount), switchMap$1(filters => {
        this.setFilter('search', [filters], 'filters');
        if (this.featureFlagsConfigService.get().filterPOHProductsInSearch) {
          const excludedFeedIDs = ['71611', '71638', '71663', '71664'];
          return this.storeSearchService.getProductsByFilters({
            ...this.storeSearchService.getStoreSearchParams(),
            search: filters.search
          }, 'search-bar').pipe(filter$1(data => {
            data.products = data.products.filter(product => {
              return product.v4ApiResponse.feeds.every(feed => !excludedFeedIDs.includes(feed.feedID));
            });
            return data.products.length > 0;
          }));
        } else {
          return this.storeSearchService.getProductsByFilters({
            ...this.storeSearchService.getStoreSearchParams(),
            search: filters.search
          }, 'search-bar');
        }
      }), takeUntilDestroyed(this.destroyRef));
    }
    setFilter(name, values, groupBy) {
      this.filter = {
        name: name,
        values: values,
        groupBy: groupBy
      };
    }
    getFilter() {
      return this.filter;
    }
    navigateToSearchPage(searchCriteria) {
      this.router.navigate(['/search'], {
        queryParams: {
          query: searchCriteria
        },
        queryParamsHandling: ''
      });
    }
    pushSearchBarEvent(keyword) {
      this.searchBarEvents$$.next(keyword);
    }
    static {
      this.ɵfac = function SearchBarService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SearchBarService)(i0.ɵɵinject(i1$1.Router), i0.ɵɵinject(i1.FeatureFlagsConfigService), i0.ɵɵinject(StoreSearchService), i0.ɵɵinject(i0.DestroyRef));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SearchBarService,
        factory: SearchBarService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SearchBarService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SpinnerService = /*#__PURE__*/(() => {
  class SpinnerService {
    constructor() {
      this.loading = false;
    }
    setLoading(loading) {
      this.loading = loading;
    }
    getLoading() {
      return this.loading;
    }
    static {
      this.ɵfac = function SpinnerService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SpinnerService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SpinnerService,
        factory: SpinnerService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SpinnerService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let NavMenuService = /*#__PURE__*/(() => {
  class NavMenuService {
    constructor(builderService, locale) {
      this.builderService = builderService;
      this.locale = locale;
      this.navContent$$ = new ReplaySubject();
      this.navContent$ = this.navContent$$.asObservable();
      this.getNavContent();
    }
    getNavContent() {
      this.builderService.getContentsByModelName('navigation-menu-config').pipe(map$1(builderContent => this.buildMenu(builderContent))).subscribe(builderContent => {
        this.navContent$$.next(builderContent);
      });
    }
    buildMenu(builderContent) {
      let menu = [];
      if (!builderContent[0].data?.navigation1) {
        return menu;
      }
      const data = builderContent[0].data?.navigation1;
      if (data) {
        menu = this.getEntry(data, []);
      }
      return menu;
    }
    getEntry(data, menu) {
      if (data) {
        data.map(d => {
          const entry = {
            dropdownStyle: d.dropdownStyle ?? null,
            title: d.menuEntry ? this.getLocaleData(d.menuEntry, this.locale) : '',
            hidden: d.visible ?? false,
            linkedPage: this.getLink(d),
            customizeLink: this.getCustomization(d),
            childLinks: this.getEntry(d.childLinks, []),
            image: d.image ?? undefined,
            skipOptimization: d.skipOptimization ?? true
          };
          menu.push(entry);
        });
      }
      return menu;
    }
    getLink(page) {
      let url = null;
      if (page.linkedPage && page.linkedPage.refPage) {
        url = page.linkedPage.refPage.value.data.url;
      } else if (page.linkedPage && page.linkedPage.link) {
        url = page.linkedPage.link;
      }
      return url;
    }
    getCustomization(page) {
      const customization = {
        underline: false,
        bold: false,
        italic: false,
        shapeColor: null,
        fontColor: null,
        image: null
      };
      if (page.customizeLink) {
        const {
          underline,
          bold,
          shapeColor,
          fontColor,
          italic,
          image
        } = page.customizeLink;
        customization.underline = underline ?? customization.underline;
        customization.bold = bold ?? customization.bold;
        customization.italic = italic ?? customization.italic;
        customization.shapeColor = shapeColor ?? customization.shapeColor;
        customization.fontColor = fontColor ?? customization.fontColor;
        customization.image = image ?? customization.image;
      }
      return customization;
    }
    getLocaleData(data, locale) {
      if (typeof data == 'object' && data['@type'] && data['@type'] == '@builder.io/core:LocalizedValue') {
        data = data['Default'] ?? '';
        if (data[locale]) {
          data = data[locale];
        }
      }
      return data;
    }
    static {
      this.ɵfac = function NavMenuService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || NavMenuService)(i0.ɵɵinject(BuilderService), i0.ɵɵinject(LOCALE_ID));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: NavMenuService,
        factory: NavMenuService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return NavMenuService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SubscribeNewsletterService = /*#__PURE__*/(() => {
  class SubscribeNewsletterService {
    constructor(customerService, alertMessageService, storeService, destroyRef) {
      this.customerService = customerService;
      this.alertMessageService = alertMessageService;
      this.storeService = storeService;
      this.destroyRef = destroyRef;
      this.hasNewsletter$ = this.storeService.hasNewsletter$;
      this.subscribed$$ = new BehaviorSubject(false);
      this.subscribed$ = this.subscribed$$.asObservable();
      this.subscribed = false;
      this.hasNewsletter$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(hasNewsletter => this.hasNewsletter = hasNewsletter);
    }
    subscribe($event) {
      this.customerService.subscribeToNewsletter$($event.email, $event.subscribe_newsletter, $event.subscribe_promotion, $event.extra).pipe(filter$1(data => data !== undefined), takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.subscribed$$.next(true);
          this.subscribed = true;
        },
        error: error => {
          this.alertMessageService.sendMessage(error.message, 'danger');
        }
      });
    }
    static {
      this.ɵfac = function SubscribeNewsletterService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SubscribeNewsletterService)(i0.ɵɵinject(CustomerService), i0.ɵɵinject(AlertMessageService), i0.ɵɵinject(StoreService), i0.ɵɵinject(i0.DestroyRef));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SubscribeNewsletterService,
        factory: SubscribeNewsletterService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SubscribeNewsletterService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SubscribeNewsletterModalService = /*#__PURE__*/(() => {
  class SubscribeNewsletterModalService {
    #platformId = inject(PLATFORM_ID);
    #logger = inject(GenericLogger);
    #document = inject(DOCUMENT);
    #subscribeNewsletterService = inject(SubscribeNewsletterService);
    get subscribedToNewsletter() {
      return this.#subscribeNewsletterService.subscribed;
    }
    #isBrowser() {
      return isPlatformBrowser(this.#platformId);
    }
    /**
     * It returns 'true' when 'hide_newsletter' is not present
     * in localstorage and if it expired.
     */
    shouldIShowModalOnLoad() {
      if (!this.#isBrowser()) {
        return false;
      }
      const newsletterLocaStorageItem = localStorage.getItem('hide_newsletter');
      if (!newsletterLocaStorageItem) {
        return true;
      }
      const item = JSON.parse(newsletterLocaStorageItem);
      if (this.#isExpired(item)) {
        return true;
      }
      return item.value === undefined || item.value != 'true';
    }
    /**
     * Removes 'hide_newsletter' item if expired.
     */
    #isExpired(item) {
      const now = new Date();
      if (now.getTime() > item.expiry) {
        localStorage.removeItem('hide_newsletter');
        return true;
      }
      return false;
    }
    /**
     * Set the 'hide_newsletter' item to 'true' in localStorage
     * and set the CSS display variable accordingly.
     */
    hideModalAndSetLocalStorage() {
      if (!this.#isBrowser()) {
        this.#logger.error('Calling hideModal() in server has no effect');
        return;
      }
      this.#setHideNewsletterItemInLocalStorage('hide_newsletter', 'true');
      this.#document.documentElement.style.setProperty('--subscribe-newsletter-display', 'none');
      this.#document.body.classList.remove('modal-open');
    }
    /**
     * Set item in localStorage with a 30 days expiration.
     **/
    #setHideNewsletterItemInLocalStorage(key, value) {
      if (!this.#isBrowser()) {
        return;
      }
      const now = new Date();
      const item = {
        value,
        expiry: now.getTime() + 30 * 24 * 60 * 60 * 1000
      };
      localStorage.setItem(key, JSON.stringify(item));
    }
    /**
     * Check if 'hide_newsletter' item is present in localStorage
     * and sets the CSS display variable accordingly.
     */
    showModal() {
      if (!this.#isBrowser()) {
        this.#logger.error('Calling showModal() in server has no effect');
        return;
      }
      this.#document.documentElement.style.setProperty('--subscribe-newsletter-display', 'block');
      this.#document.body.classList.add('modal-open');
    }
    static {
      this.ɵfac = function SubscribeNewsletterModalService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SubscribeNewsletterModalService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SubscribeNewsletterModalService,
        factory: SubscribeNewsletterModalService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SubscribeNewsletterModalService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let BuilderDependencyDirective = /*#__PURE__*/(() => {
  class BuilderDependencyDirective {
    constructor(meCachisService) {
      this.meCachisService = meCachisService;
    }
    ngOnInit() {
      this.meCachisService.addBuilderDependency(this.name);
    }
    static {
      this.ɵfac = function BuilderDependencyDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BuilderDependencyDirective)(i0.ɵɵdirectiveInject(MECACHIS_DEPENDENCY_SERVICE));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: BuilderDependencyDirective,
        selectors: [["", "sfBuilderDependency", ""]],
        inputs: {
          name: "name"
        },
        standalone: true
      });
    }
  }
  return BuilderDependencyDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * TODO: This directive is not the best approach to handle the revalidation of the builder cache.
 * As the API call will not replicate the same behavior for the SSR and for the revalidation dependency fetch.
 * But we probably can handle some extra invalidations as the new BuilderSDK will not need this pipe.
 * If we do not want the extra invalidations, we can use the simple builderDependency.
 */
let RevalidableBuilderDependencyDirective = /*#__PURE__*/(() => {
  class RevalidableBuilderDependencyDirective {
    #logger = inject(GenericLogger);
    constructor(meCachisService, component) {
      this.meCachisService = meCachisService;
      this.component = component;
    }
    onContentLoad(event) {
      const {
        id,
        lastUpdated
      } = event ?? {};
      const apiKey = this.component.builderService?.apiKey$.value;
      if (lastUpdated && id && apiKey) {
        this.meCachisService.addRevalidableBuilderDependency(this.name, {
          contentToRevalidate: [{
            id,
            lastUpdated
          }],
          url: `https://cdn.builder.io/api/v3/content/${this.name}?apiKey=${apiKey}`,
          apiKey,
          queryType: 'content',
          querySearchParams: new URLSearchParams({
            'query.id': id
          }),
          timeToRevalidate: 60 * 1
        });
      } else {
        this.#logger.warn({
          msg: 'RevalidableBuilderDependencyDirective: No lastUpdated id or apiKey found',
          event
        });
      }
    }
    static {
      this.ɵfac = function RevalidableBuilderDependencyDirective_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || RevalidableBuilderDependencyDirective)(i0.ɵɵdirectiveInject(MECACHIS_DEPENDENCY_SERVICE), i0.ɵɵdirectiveInject(i1$2.BuilderComponentComponent, 9));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: RevalidableBuilderDependencyDirective,
        selectors: [["", "sfRevalidableBuilderDependency", ""]],
        hostBindings: function RevalidableBuilderDependencyDirective_HostBindings(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵlistener("load", function RevalidableBuilderDependencyDirective_load_HostBindingHandler($event) {
              return ctx.onContentLoad($event);
            });
          }
        },
        inputs: {
          name: "name"
        },
        standalone: true
      });
    }
  }
  return RevalidableBuilderDependencyDirective;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SpinnerInterceptor = /*#__PURE__*/(() => {
  class SpinnerInterceptor {
    constructor(samAngularService, spinnerService) {
      this.samAngularService = samAngularService;
      this.spinnerService = spinnerService;
      this.totalRequests = 0;
      this.samContext = this.samAngularService.getContext();
    }
    intercept(request, next) {
      // We just want to apply the interceptor if we are trying to get all the properties for the contact form with Zendesk
      if (!request.url.match(`${this.samContext.apiContext.v4.zendeskGetFormBaseEndpoint}/`)) {
        return next.handle(request);
      }
      this.totalRequests++;
      this.spinnerService.setLoading(true);
      return next.handle(request).pipe(finalize(() => {
        this.totalRequests--;
        if (this.totalRequests == 0) {
          this.spinnerService.setLoading(false);
        }
      }));
    }
    static {
      this.ɵfac = function SpinnerInterceptor_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SpinnerInterceptor)(i0.ɵɵinject(SamAngularService), i0.ɵɵinject(SpinnerService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SpinnerInterceptor,
        factory: SpinnerInterceptor.ɵfac
      });
    }
  }
  return SpinnerInterceptor;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
const memorizerInterceptor = (req, next) => {
  if (req.method !== 'GET') {
    return next(req);
  }
  const memoizeService = inject(MeCachisMemoizeServerSideService);
  const cacheResponsePromise = memoizeService.get(req.url);
  return from(cacheResponsePromise).pipe(switchMap$1(cachedResponse => {
    if (cachedResponse) {
      const responseObject = new HttpResponse(cachedResponse);
      // Return the cached response as an observable
      return of(responseObject);
    }
    return next(req).pipe(tap$1(event => {
      if (event instanceof HttpResponse) {
        memoizeService.set(req.url, event);
      }
    }));
  }));
};
let StaticFileHash = /*#__PURE__*/(() => {
  class StaticFileHash {
    constructor(storeSpecificService) {
      this.storeSpecificService = storeSpecificService;
    }
    transform(filePath) {
      return filePath + '?buildId=' + this.storeSpecificService.get().buildId;
    }
    static {
      this.ɵfac = function StaticFileHash_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || StaticFileHash)(i0.ɵɵdirectiveInject(i1.StoreSpecificConfigService, 16));
      };
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "staticFileHash",
        type: StaticFileHash,
        pure: true,
        standalone: true
      });
    }
  }
  return StaticFileHash;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function provideMecachisMemoize(meCachisMemoize) {
  const provideMecachisMemoize = makeEnvironmentProviders([MeCachisMemoizeServerSideService, {
    provide: MECACHIS_MEMOIZE,
    useValue: meCachisMemoize
  }]);
  return provideMecachisMemoize;
}

/**
 * this mock class is only created to be provided in the test module where Analytics service is used
 */
class AnalyticsServiceMock {
  constructor() {
    this.events$ = of();
  }
}
class AuthServiceMock {
  getLegacyStoreCredentials$() {
    return of({
      accessToken: '',
      expiresIn: 12
    });
  }
  getStoreToken$() {
    return of({
      accessToken: '',
      expiresIn: 12
    });
  }
  getM2MToken$() {
    return of({
      accessToken: '',
      expiresIn: 12
    });
  }
}

/**
 * this mock class is only created to be provided in the test module where Builder service is used
 */
class BuilderServiceMock {
  getContentByModelName(modelName) {
    return of('');
  }
  getContentsByModelName(modelName, params) {
    return of('');
  }
  isBuilderResults(builderContent) {
    return true;
  }
  getPageByParams(builderParams) {
    return of('');
  }
  async prefetchMostPopularPages() {
    return [];
  }
  getPageRequestParams(path, queryParams) {
    return {};
  }
}
const customerMock = {
  genre: 'Mr',
  firstName: 'John',
  lastName: 'Doe',
  dateOfBirth: 1,
  email: `test.${Math.floor(Math.random() * 1000)}@scalefast.com`,
  password: 'password',
  newsletter: true,
  promotions: false,
  termsAndConditions: true,
  type: 'individual',
  country: 'ES'
};
const productCategoriesMock = [{
  categoryID: '1',
  categoryName: 'Cat 1',
  externalID: 'ext_1',
  parent_cat_id: '0'
}, {
  categoryID: '2',
  categoryName: 'Cat 2',
  externalID: 'ext_2',
  parent_cat_id: '1'
}];
const filtersConfigurationMock = [{
  value: 'color',
  name: '',
  type: '',
  groupBy: '',
  options: [{
    value: 'red',
    checked: false,
    label: ''
  }, {
    value: 'blue',
    checked: false,
    label: ''
  }],
  excludedCategories: [1]
}, {
  value: 'size',
  name: '',
  type: '',
  groupBy: '',
  options: [{
    value: 'small',
    checked: false,
    label: ''
  }, {
    value: 'medium',
    checked: false,
    label: ''
  }],
  excludedCategories: []
}];
const filtersMock = {
  offset: 1,
  pageSize: 12,
  filters: [{
    name: 'color',
    values: ['red'],
    groupBy: ''
  }, {
    name: 'size',
    values: ['small', 'medium'],
    groupBy: ''
  }]
};
const productMock = {
  categories: [{
    categoryID: '1',
    categoryName: 'Cat 1',
    externalID: 'ext_1',
    parent_cat_id: '0'
  }, {
    categoryID: '2',
    categoryName: 'Cat 2',
    externalID: 'ext_2',
    parent_cat_id: '1'
  }],
  country: 'ROW',
  descriptions: [{
    lang: 'EN',
    title: 'Product title',
    short: 'Product short description',
    long: 'Product long description',
    seo: {
      meta_description: 'Meta description',
      title: 'SEO title',
      url: 'SEO url'
    },
    sections: [{
      i18nTitleTag: 'PRODUCTS.DESCRIPTION',
      content: 'Product description'
    }]
  }],
  hiddenPrice: false,
  id: '1',
  imageUrl: 'https://via.placeholder.com/1480x570.jpg',
  gallery: [{
    name: 'Image name',
    nameAlt: 'Image alt',
    type: 'Image type',
    url: 'https://via.placeholder.com/1480x570.jpg',
    visible: true
  }],
  state: 'OK',
  price: {
    country: 'ROW',
    currency: 'EUR',
    currencies: [{
      currency: 'EUR',
      prices: [{
        recommendedPrice: '0',
        unitCost: '0',
        licence: 'NL',
        price: 48.9,
        oldPrice: 0
      }]
    }],
    includesTaxes: true,
    taxType: 'default',
    prices: [{
      recommendedPrice: '0',
      unitCost: '0',
      licence: 'NL',
      price: 48.9,
      oldPrice: 0
    }]
  },
  stock: {
    lowStockAlert: 2,
    policy: 'SHOW',
    stock: 10
  },
  type: 'PHYSICAL'
};

/**
 * Generated bundle index. Do not edit.
 */

export { AddToCartService, AlertMessageService, AnalyticsService, AnalyticsServiceMock, AuthService, AuthServiceMock, BreadcrumbService, BrowserLoggerService, BuilderDependencyDirective, BuilderPrefetchService, BuilderService, BuilderServiceMock, CartService, CategoryPageService, ContactService, CrossSellProductsService, CustomerService, DATE_NOW_FUNCTION, DependencyBuilder as DependencyMother, EXPRESS_REQUEST, EXPRESS_RESPONSE, Fixtures, GenericLogger, IS_BUILDER_EDITOR, LanguageSelectorService, LayoutService, LegacyV4ConfigService, LocalStorageService, LocalizeHelper, LoginService, MAINTENANCE_MODE_CONFIG, MECACHIS_DEPENDENCY_COLLECTION, MECACHIS_DEPENDENCY_SERVICE, MECACHIS_MEMOIZE, METAPRODUCT_SELECTOR_SERVICE, MaintenanceService, MaintenanceServiceMock, MeCachisDependencyServerSideService, MeCachisDependencyServiceNoOp, MeCachisMemoizeServerSideService, MetaproductSelectorCheapestService, MetaproductSelectorFirstAvailableService, NavMenuService, PAGE_DATA, PAGE_RESOLVERS, PREFETCH_SERVICES, PRODUCT_SLUGGER_SERVICE, PageBuilderResolver, PageNotFoundResolver, PrefetchService, ProductAddedToCartService, ProductBreadcrumbService, ProductCarouselService, ProductGalleryService, ProductLogger, ProductPriceService, ProductResolverService, ProductService, ProductShortDescriptionService, ProductSluggerService, ProductTitleService, RevalidableBuilderDependencyDirective, SERVER_LOGGER, SERVER_PROVIDERS_REQUIRED, SamAngularService, SearchBarService, SearchResultsPageService, ServerLoggerService, ServerNavigateService, ServerResponseService, SpinnerInterceptor, SpinnerService, StaticFileHash, StoreSearchService, StoreService, SubscribeNewsletterModalService, SubscribeNewsletterService, UniversalLocationService, WishlistService, customerMock, filtersConfigurationMock, filtersMock, imageLoader, isBuilderPageResults, memorizerInterceptor, pageResolver, productCategoriesMock, productMock, provideMecachisMemoize, providePageResolvers, providePrefetch, sluggerTestDouble };
